// ============================================================
// TERROR — THE UNSTABLE WORLD  (world data)
// The overworld data, the requirement resolver, and the stamps.
// ------------------------------------------------------------
// THE TRUE STORY MODE. Canon spine, straight from Onboarding
// ACT VII/VIII: J'ynxi's seal weakened, two kids played the
// Solrend, and "now J'ynxi is watching over them." The player
// and their partner are those kids' PARENTS — Willy senses
// "both of yours' King's Grace after I mentioned your esclings."
// Your children are Esclings. They are out there. You walk the
// Unstable World until you find them, and finding them walks
// you into J'ynxi.
//
// ══ THE PAINTED WORLD (July 29, 2026) ═══════════════════════
// caP'n shipped `terror_ow_1.webp` — a finished, hand-painted SMW
// overworld — and it replaced the procedural one wholesale.
// `worldRender.js` is DELETED. Three things follow from that,
// and they are the whole diff on this file:
//
//   1. A NODE'S POSITION IS NOW A PIXEL, NOT A PROJECTION.
//      Coordinates used to be region-local (`x` 0..100, `y`
//      0..region.h) and got projected onto procedural islands.
//      There are no procedural islands any more. Every node
//      now carries `mx`/`my` — ABSOLUTE pixels in the painting's
//      own 1054x1492 space (see MAP below). The renderer scales
//      that space to the viewport and does nothing else to it.
//
//      The rename from x/y to mx/my is deliberate and is the
//      cheap safety net: anything still reading the old fields
//      gets `undefined` and fails LOUDLY, instead of silently
//      dropping a pin at 0,0 or at half-scale. There is no
//      correct way to reinterpret an old x=50 in the new space,
//      so nothing is allowed to try.
//
//   2. REGIONS ARE NAMED BY THE PAINTING. The zone names are
//      painted into the pixels — we cannot render "The Outer
//      Veil" over an island that says BLIGHTED MARSH. The art
//      wins. Region `id`s are UNCHANGED (they are Firebase-
//      adjacent and appear in save data); only `name` moved.
//
//   3. THE `terrain` BLOCKS ARE GONE. Every seed, ramp, sun,
//      river, pool, wood, fauna and star count in this file
//      existed for exactly one consumer — the generator — and
//      that consumer no longer exists. ~270 lines deleted.
//      `brainElo`, `color`, `hue` and `flavor` stay; those are
//      game data, not terrain data.
//
// ── THE FLOOR LAW (inherited from H23) ──────────────────────
// Every node is playable the day it is written down. Authored
// nodes carry a named boss out of the existing cast; every other
// node rolls a Depths-style procedural opponent at its own
// difficulty. An unauthored node is not broken — it is CLASSICAL.
// Authoring is enrichment, never a prerequisite for shipping.
//
// ── THE SEAM LAW (the Exception Law, applied) ───────────────
// Nothing here is an enum of what exists today. A path is FOUR
// orthogonal knobs now — `via` (what event opens it), `req`
// (what else must be true), `hidden` (whether the player can see
// it closed), and `bend` (waypoints the road bows through). A
// requirement is a recursive data object, not a switch. Adding
// "a route that only opens for a Machine-ilk Lord wearing a
// Radiant sheen" is a data row, not an engine change.
//
// ── WHAT LIVES WHERE ────────────────────────────────────────
// PURE DATA + PURE FUNCTIONS + the Firebase stamps for this one
// screen. No React. No rules engine. No currency writes beyond
// the existing awardCoins/addCardToCollection helpers.
//
// ── THE FILENAME, DELIBERATELY ──────────────────────────────
// This module is `worldData.js`, not `worldMap.js`, purely so it
// cannot collide with `WorldMap.js` on a case-insensitive
// filesystem — which is every Windows box, including the one these
// files get pasted into. Two files differing only in case is one
// file there, and the second one silently eats the first. The
// Firebase node below stays `worldMap` — that is a database key,
// not a filename, and it reads better.
//
// FIREBASE: profiles/{uid}/worldMap/*  — a NEW TOP-LEVEL PROFILE
// FIELD, which per IE_FIREBASE_LAW §1 needs NO rules deploy (the
// hard $extra lock lives only on collection/$cardId; every other
// player-level field is permitted-but-unvalidated by design).
// Append-only: every clear/secret/child is a runTransaction stamp
// on its exact path that ABORTS if already present, so a
// double-tap can never double-pay. Nothing here is ever deleted
// or set() wholesale.
// ============================================================

import { ref, get, update, runTransaction } from "firebase/database";
import { db } from "./firebase";
import { awardCoins, addCardToCollection, pourMinerium1, grantFirstBlood, mirrorLeaderboard, unlockCompanion, grantCrests, appendShelfItem, STORY_TROPHY_BY_BOSS } from "./progression";
import { labelForNode } from "./minerium1Rewards";   // THE BLUEPRINT — the room name for the ribbon; the map and the pour both live elsewhere
import { GATEKEEPERS, generateOpponent } from "./EndlessDepths";
import { sceneFor } from "./minerium1";   // what the cast says HERE — see minerium1.js
import { OPPONENTS, INNER_VILLAINS } from "./storyData";
import { getAllGrimoireCards } from "./cards";
import { TOOL_KEEP, LEDGER_KEEP } from "./vaelsTools";   // V'ael's Veil and whatever she lends next — see vaelsTools.js

// ═══════════════════════════════════════════════════════════════
// THE MAP
// ═══════════════════════════════════════════════════════════════
// The painting's native pixel size. EVERY `mx`/`my` on this page
// is expressed in this space and nothing else, so a coordinate
// read off the art in an image editor is a coordinate you can
// paste straight in. The screen scales; the numbers never do.
//
// If the art is ever re-exported at a different size, change these
// two numbers and every pin stays exactly where it was painted —
// they are proportional, not absolute-on-screen.

// ═══════════════════════════════════════════════════════════════
// ⬡ THE MINERIA — the world is PLURAL, and it always was
// ═══════════════════════════════════════════════════════════════
// The roadmap says Minerium 2 "is a second map + minerium2.js" and
// that the overlay system means it costs no engine work. Half true,
// and the half that is false is worth writing down where the next
// session will find it.
//
//   TRUE:  the SCRIPT costs no engine work. minerium1.js proved the
//          overlay shape — a file keyed by node id, merged in
//          sceneFor/opponentForNode. minerium2.js is content.
//   TRUE:  the DOOR costs no engine work. WORLD_GATES is a table and
//          takes a second doorman as one row. EMPERIUMS likewise.
//   FALSE: "a second map" is not a free noun. Until 2026-08-19 this
//          file exported ONE `MAP` const, and WorldMap.js reads it
//          at MODULE SCOPE inside its camera clamp. Two paintings
//          was never a data change; it was a camera change.
//
// So the assumption gets a name. `MINERIA` is the table of
// paintings, and everything a world owns hangs off its row. Today it
// has one entry and every downstream export is byte-identical to
// what it was — `MAP` is still Minerium 1's painting, `NODES` is
// still every node, `PATHS` is still every road. This is inert on
// purpose: a structural change that alters no behaviour is the only
// kind worth making without a browser open.
//
// ── WHY NODES AND PATHS STAY GLOBAL ──────────────────────────────
// It is tempting to nest them per world. Don't. Node ids are already
// globally unique and EVERY stamp in Firebase is keyed by node id —
// cleared/{nodeId}, secrets/{nodeId}, shards/{nodeId}. Nesting the
// tables would either duplicate that key space or force a migration
// on every existing save to rename something nobody can see. The
// same reasoning that keeps `emperiums/xaol` spelled "xaol" applies
// here, and it applies harder.
//
// Counters stay global for the same reason they read right: SHARD_TOTAL,
// SECRET_NODES and CHILDREN are "the whole story," not "this map."
// A player who has walked one of three Mineriums should see 27/81
// shards, because that is the truth.
//
// ── ⭐ WHAT MINERIUM 2 ACTUALLY NEEDS, IN ORDER ──────────────────
// Not a memo — a checklist. When the painting lands:
//
//   1. `terror_ow_2.webp` on terror-assets, and its native pixel
//      size. THIS IS THE ONLY BLOCKING ITEM AND IT IS caP'n's.
//      Nothing below can be authored against a painting that does
//      not exist, because every pin is a coordinate ON it.
//   2. A row in MINERIA below. Two lines.
//   3. Regions: rows in REGIONS with their own ids and hues.
//   4. Nodes: rows in RAW_NODES carrying `world: "<id>"`, and their
//      coordinates in PIN — dragged in `world_pinner.html`, which
//      prints the block. Do not hand-type coordinates.
//   5. Roads: rows in RAW_PATHS. They inherit their world from
//      `from`, so they need no world field of their own.
//   6. The doorman: one row in WORLD_GATES.
//   7. The Lord's door: one row in EMPERIUMS. Its `wardens` array is
//      however many wardens THIS painting has — four is a fact about
//      Minerium 1's Crimson Dais, not about Mineriums.
//   8. The link: one row in WORLD_LINKS below, so the Champion's
//      "door that was not there" is a door and not a line.
//   9. `minerium2.js`, same shape as minerium1.js.
//  10. ⚠ THE ONE REAL CODE CHANGE — WorldMap.js. `MAP` is read at
//      module scope by zFitFor / zFloorFor / zCeilFor / clampCamTo /
//      fitWorld and by the render's plane, art <img> and <svg>. Each
//      must take the ACTIVE world's map instead. `NODES` and `PATHS`
//      at the render and travel sites become `nodesIn(worldId)` and
//      `pathsIn(worldId)`. That is roughly twenty-five substitutions
//      in one file and it wants a live lap, because a missed call
//      site does not throw — it silently clamps the camera to the
//      wrong painting's dimensions, which looks like "the map is
//      stuck" and reads like a mystery.
//
// Steps 2-9 are data. Step 10 is the session. Step 1 is caP'n's.
export const MINERIA = [
  {
    id: "hosh_vrown",
    name: "The Minerium of Sir H'osh Vrown",
    // The painting's native pixel size. EVERY `mx`/`my` on this page
    // is expressed in this space and nothing else, so a coordinate
    // read off the art in an image editor is a coordinate you can
    // paste straight in. The screen scales; the numbers never do.
    //
    // If the art is ever re-exported at a different size, change
    // these two numbers and every pin stays exactly where it was
    // painted — they are proportional, not absolute-on-screen.
    map: { w: 841, h: 1264, file: "terror_ow_1.webp" },
    // The world a save with no world on it belongs to. Exactly one
    // row may carry this, and it is what makes the whole table inert
    // for every player who is already mid-story.
    first: true,
  },
];

// Back-compat, and the whole reason this refactor is inert: every
// existing reader of `MAP` still gets Minerium 1's painting, byte for
// byte. It is declared AFTER the table on purpose — a `const` read
// before its own declaration is a TDZ ReferenceError at module load,
// which in a bundled React app presents as a blank white screen with
// a stack trace pointing at the wrong file entirely.
export const MAP = MINERIA[0].map;

export const MINERIUM_BY_ID = Object.fromEntries(MINERIA.map(m => [m.id, m]));
export const FIRST_WORLD = (MINERIA.find(m => m.first) || MINERIA[0]).id;
export const mapOf = (worldId) => (MINERIUM_BY_ID[worldId] || MINERIA[0]).map;
export const mineriumName = (worldId) => MINERIUM_BY_ID[worldId]?.name || null;

// ── WORLD LINKS — the door the Champion steps aside from ────────
// "*He steps aside from a door that was not there.*" (minerium1.js,
// m1_champion). Today that door is a line of prose with nothing
// behind it, which is correct — there is nowhere for it to go. The
// day there is, it is this row and not a new mechanism.
//
// `after` is a node id: clearing it opens the link. It is
// deliberately a NODE and not a beat, because a beat is a cutscene
// and a cutscene can be skipped, and a door that depends on somebody
// having read something is a door that will be reported as broken.
export const WORLD_LINKS = [
  // { id: "m1_to_m2", from: "hosh_vrown", to: "<id>",
  //   after: "dais_jewel", enter: "Go there instead.",
  //   line: "A door that was not there." },
];

export const worldLinksOpen = (ctx) =>
  WORLD_LINKS.filter(l => !l.after || ctx.cleared.has(l.after));

// ═══════════════════════════════════════════════════════════════
// ⚠ THE SURVEYOR — THE TEST DIAL  (caP'n, July 29: "since it's
//   testing I need to be able to walk around properly from node to
//   node and use the teleports")
// ═══════════════════════════════════════════════════════════════
// TRUE  : every gate in the world is held open. Every road walks,
//         every secret exit is found, every warp is charged, every
//         requirement passes, and no stop is ever locked. You can
//         walk the whole 59-node graph on a brand-new save without
//         winning a single duel, and the Node panel grows a JUMP
//         HERE button so the far side of the map is one tap away.
//         The map is an INSTRUMENT in this mode, not a game.
//
// FALSE : shipping behaviour, described in full below. This is
//         what every production build gets, unconditionally.
//
// ── WHY THE MAP "DOESN'T WORK" ON A FRESH SAVE ────────────────
// It does work. It is doing exactly what it was told, and the rule
// is worth stating plainly because it is invisible from the screen:
//
//   A path's gate is its `from` node, and the default `via` is
//   "clear". So a road opens when the stop it leaves FROM has been
//   beaten — and it stays shut in BOTH directions until then.
//
// There are 87 paths in this file and exactly ZERO of them carry
// `via: "open"`. On a new save nothing is cleared, so from The
// Roadhead the count of legal moves is zero, and the correct
// player experience is: fight the Roadhead, win, and the Marsh
// opens. That is the design. It is also completely useless for
// checking whether a pin in the Obsidian Keep sits on its painted
// pad, which is what this dial is for.
//
// ── WHAT THIS DIAL IS NOT ─────────────────────────────────────
// It is NOT a cheat that writes anything. Clears, secrets and
// found-children are still only ever stamped by winning a real
// duel, through the same transaction-guarded append-only writes.
// Turning SURVEYOR off returns the save to exactly the progress
// that was honestly earned — nothing to undo, nothing to migrate.
// The one thing it touches is the position you last stood on,
// because walking is the point.
//
// ── IT CANNOT SHIP ON ANY MORE ────────────────────────────────
// This used to be a bare `export const SURVEYOR = true`, and the
// close-out list carried a line reading "the single flag that must
// flip before a player sees the Overworld." That line was the
// problem. A safety that depends on somebody remembering something
// is not a safety, it is a reminder, and the failure it guards
// against is EXACTLY the one a reminder cannot prevent: flip it on
// to test one node, get pulled into three other things, ship.
//
// So the remembering is deleted rather than the bug. The dial below
// still does what it always did and is still the one thing to
// touch — but it is ANDed with the environment, and a deployed
// build forces it false no matter what the dial says. caP'n can
// leave it on for a week, commit it on, and the shipped game is
// still locked. Localhost is the only place it can speak.
//
// ── WHY IT ASKS THE BROWSER AND NOT THE BUNDLER ───────────────
// ⚠ THE BUG THIS LINE ALREADY CAUSED ONCE (July 30). The first
// version of this gate read:
//
//     typeof process !== "undefined" && process.env.NODE_ENV === …
//
// and it locked the world on `npm start` too. Webpack 5 substitutes
// the LITERAL text `process.env.NODE_ENV` at build time, but it
// dropped webpack 4's automatic Node shims — so there is no
// `process` OBJECT in a browser bundle, `typeof process` is
// "undefined", the whole chain short-circuits, and dev reads as
// production. It failed closed exactly as designed. It just failed
// closed everywhere, silently, with nothing in the console, and
// what caP'n saw was a world where every one of 59 pins had gone
// grey and no arrow key did anything.
//
// The comment two paragraphs up had literally predicted it — "a
// future bundler with no `process` at all reads as production."
// The future bundler was the one already installed.
//
// So the gate stops asking the toolchain a question the toolchain
// answers differently per version, and asks the browser a question
// that has exactly one answer: WHERE AM I RUNNING? A hostname is a
// runtime fact. It cannot be tree-shaken, substituted, polyfilled
// or optimised away, it does not care which bundler is in front of
// it, and it cannot be "localhost" on terror.pages.dev.
//
// STILL FAILS CLOSED. Anything that is not an explicitly listed
// local host — a deploy, a preview URL, a LAN IP, a file:// open,
// a server-side render with no `window` at all — reads as shipping
// and keeps the world shut. A test mode that stays off when it
// should have been on costs one flipped constant. The other
// direction costs the whole game.
//
// ── THE COST CAME DUE (caP'n, July 30 2026, second pass) ──────
// "the nodes and pathing is gone again in the Story Mode xD"
//
// The paragraph above priced this at "one flipped constant." It was
// wrong, and the reason is worth writing down: THERE IS NO CONSTANT
// TO FLIP. `SURVEYOR_DIAL` was already true. The dial had been on
// the whole time and the environment was quietly overruling it, so
// the documented remedy did nothing, twice, and the symptom read as
// data loss instead of a gate — 59 grey pins looks identical to a
// world that forgot its pins, which is the same confusion the
// missing-art measuring plane exists to break.
//
// The audit says the data is fine and always was: 59 nodes, 59
// pins, zero unpinned; 87 paths, zero pointing at a node that
// doesn't exist; 54 bends, zero orphaned; `veil_camp` still start.
// Nothing was ever lost. The world was shut.
//
// What was missing is a way to say YES, ON PURPOSE, RIGHT NOW from
// a machine that isn't localhost — a phone on the LAN, a preview
// URL, a tunnel. So the hostname list does NOT get widened; that
// list is a deliberate law and quietly reversing it is how a
// safety rots. It gets a second door instead, and the door has to
// be opened by hand every session:
//
//     ?surveyor=1     turn it on  — sticks for this TAB only
//     ?surveyor=0     turn it off — clears immediately
//
// Deliberate to open, impossible to leave open. It rides in
// sessionStorage, so it survives navigating into Story Mode and
// dies the moment the tab closes. Nobody arrives here by accident;
// a player would have to type a parameter that appears nowhere in
// the game. And the SURVEYOR badge on the map still says out loud
// that it's on, because a test mode you cannot see is a bug.
//
// AND THE DIAL IS LOAD-BEARING AGAIN, which is the part that
// matters. Under the old gate `SURVEYOR_DIAL` was decorative —
// false on a deploy no matter what — so it had stopped being a
// kill switch. Now the override is ANDed with it too. Set the dial
// false before a public launch and BOTH doors are welded, hostname
// and query string alike. One line, one meaning, still the only
// line to touch.
const SURVEYOR_DIAL = true;   // ⚠ ON by caP'n's request 2026-08-18: "turn surveyor on so I can traverse the map after the first duel with SHV". Still ANDed with IS_DEV_BUILD below, so a production build stays gated no matter what this says.   // ← the only line to touch

const DEV_HOSTS = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\]|::1)$/;

// Exported since 2026-08-20 so other screens can gate a dev-only
// affordance on the same signal the Surveyor uses, instead of each
// inventing its own idea of "am I in development".
export const IS_DEV_BUILD =
  typeof window !== "undefined" &&
  !!window.location &&
  DEV_HOSTS.test(String(window.location.hostname || ""));

// The second door. Wrapped whole because blocked storage (Safari
// private browsing, a locked-down webview) throws on read, and a
// throw at module load takes the entire world map down with it —
// a strictly worse failure than the one this is fixing. Anything
// unexpected in here means false, which is shut.
const SURVEYOR_KEY = "terror.surveyor";

// ⚠ THIS WAS OPEN ON PRODUCTION AND THE COMMENT ABOVE SAID IT WAS NOT.
//
// The banner at the top of this block reads "Now the override is ANDed
// with it too... Set the dial false before a public launch and BOTH
// doors are welded." The expression underneath it was:
//
//     SURVEYOR_DIAL && (IS_DEV_BUILD || surveyorOverride())
//
// which is an OR, not an AND. SURVEYOR_DIAL is currently `true` by
// caP'n's own 2026-08-18 request, so on the shipped domain anybody who
// typed `?surveyor=1` got every requirement passed (line ~1633
// `if (SURVEYOR) return { ok: true }`), every node open (~1827
// `if (SURVEYOR) return "open"`) and the JUMP HERE button on the world
// map. The entire 59-node story was skippable on a brand-new save
// without winning one duel. HORIZON's LAST GATE calls this the item
// that "cannot ship open." It was the one that already had.
//
// THE OVERRIDE IS NOW TRI-STATE, and the asymmetry is the whole point:
//
//   ?surveyor=1   turn it ON  — honoured ONLY in a dev build
//   ?surveyor=0   turn it OFF — honoured EVERYWHERE, always
//   (absent)      follow the dial
//
// Opening a door is a privilege; closing one is a right. And the OFF
// direction is a small gift to the launch checklist: "TURN SURVEYOR
// OFF" on the LAST GATE now costs one URL parameter to rehearse
// instead of a code edit and a rebuild.
//
// Returns true | false | null. Null means "no opinion, use the dial".
function surveyorOverride() {
  if (typeof window === "undefined" || !window.location) return null;
  try {
    const q = new URLSearchParams(window.location.search || "").get("surveyor");
    if (q === "0" || q === "off") {
      window.sessionStorage.setItem(SURVEYOR_KEY, "0");
      return false;
    }
    if (q === "1" || q === "on") {
      window.sessionStorage.setItem(SURVEYOR_KEY, "1");
      return true;
    }
    const stored = window.sessionStorage.getItem(SURVEYOR_KEY);
    if (stored === "0") return false;
    if (stored === "1") return true;
    return null;
  } catch {
    return null;
  }
}

const _surveyorAsked = surveyorOverride();

export const SURVEYOR =
  _surveyorAsked === false           // OFF is honoured anywhere, by anyone
    ? false
    : SURVEYOR_DIAL && IS_DEV_BUILD && (_surveyorAsked !== false);

// ═══════════════════════════════════════════════════════════════
// REGIONS
// ═══════════════════════════════════════════════════════════════
// `hue` is the pin/route colour for the region and used to live in
// the generator's ISLANDS table. It moved here because it is the
// last thing on that table that was ever GAME data — a region's
// colour shows up in the region rail and on every pin, both of
// which outlived the terrain.
//
// `brainElo` feeds skillFromElo() for this region's procedural
// opponents: deeper region, better play, same rulebook.
//
// ── NAMES: THE PAINTING IS CANON ───────────────────────────────
// Left column is what the player reads; the `id` beside it is what
// the save file has always said and must keep saying.

// ═══════════════════════════════════════════════════════════════
// THE ZONES — caP'n's own trace, 2026-08-21
// ═══════════════════════════════════════════════════════════════
// ⭐ THE MAP WAS RE-TRACED BY HAND AND THIS TABLE IS THE RESULT.
// caP'n: *"The original path you made was very close but was off a
// bit, so I realized I should've just traced it myself and showed you
// so you can just copy it."* The red ink on that trace is these nine
// regions; the yellow ink is RAW_PATHS.
//
// ── THE NODE CODE, AND WHY IT IS NOW THE NODE ID ────────────────
//   A A C - 01
//   │ │ │   └── the number caP'n assigned, 1 → 56, unique in the map
//   │ │ └────── the TYPE: C Cursed Land · S Sol King · L Lord's Chamber
//   │ └──────── the ZONE, the letters below
//   └────────── the MINERIUM. Everything here is A. Minerium 2 is B.
//
// The ids USED to be hand-written slugs (`veil_camp`, `dais_jewel`).
// They are the codes now, because caP'n numbered the whole map and a
// shared vocabulary that survives being said out loud beats a slug
// only the file knows. *"Now we can say the code and understand which
// node no matter what map is being worked on."*
//
// ⚠ EVERY OLD SAVE'S WORLD PROGRESS IS ORPHANED BY THIS. The stamps
// live at profiles/{uid}/worldMap/cleared/{nodeId} and every id
// changed, so a save from before today reads as a fresh world. That is
// acceptable ONLY because Terror is pre-alpha and caP'n has said
// plainly he wipes accounts without warning. It would not be
// acceptable a day after launch.
//
// ── ORDER IS THE LADDER. `regionTier` reads this array's index, and
// the vanguard (how much board an opponent opens holding) reads that.
// So the order below is the intended march, not alphabetical accident:
// the two Marsh zones and Boneyard North sit where their difficulty
// puts them rather than where their letters do.
export const REGIONS = [
  {
    id: "boneyard_south",
    name: "Boneyard South",
    subtitle: "Zone A",
    color: "#6b7f5a",
    hue: "#cfd8c5",
    brainElo: 400,
    ilks: ["Dragon", "Time"],
    flavor: "Where the Minerium stacked what it was finished with. The road out of the Plaza ends in it, which is somebody's idea of a welcome.",
  },
  {
    id: "marsh_west",
    name: "Harsh Marsh West",
    subtitle: "Zone B",
    color: "#14b8a6",
    hue: "#7fe3d0",
    brainElo: 700,
    ilks: ["Phantom", "Space"],
    flavor: "Standing water that has not moved in an age, and trees that went on growing anyway. Nothing here is in a hurry and nothing here is asleep.",
  },
  {
    id: "mausoleum",
    name: "SHV's Mausoleum",
    subtitle: "Zone C",
    color: "#7c3aed",
    hue: "#b8a0ff",
    brainElo: 1000,
    ilks: ["Time", "Phantom"],
    flavor: "He built it for himself before he had anything worth burying. The staff have been keeping it immaculate ever since, for a man who has not died and does not visit.",
  },
  {
    id: "marsh_east",
    name: "Harsh Marsh East",
    subtitle: "Zone D",
    color: "#0d9488",
    hue: "#6fd0c0",
    brainElo: 1100,
    ilks: ["Phantom", "Space"],
    flavor: "The same water, the far side. One arch, one keeper, and a very long time to think about the first three letters of the word patience.",
  },
  {
    id: "desert",
    name: "Deserted Desert",
    subtitle: "Zone E",
    color: "#a855f7",
    hue: "#e8c48a",
    brainElo: 1400,
    ilks: ["Time", "Phantom"],
    flavor: "Sand with amethyst coming up through it like a crop. It was deserted twice — once by whoever lived here, and once by the hour, which stopped crossing.",
  },
  {
    id: "boneyard_north",
    name: "Boneyard North",
    subtitle: "Zone F",
    color: "#8b8070",
    hue: "#e8e0d0",
    brainElo: 1800,
    ilks: ["Dragon", "Time"],
    flavor: "The south yard is where things were put. The north yard is where things came to be put, under their own power, having decided.",
  },
  {
    id: "crystal",
    name: "SHV's Crystal Mausoleum",
    subtitle: "Zone G",
    color: "#8b5cf6",
    hue: "#d6c9ff",
    brainElo: 2200,
    ilks: ["Space", "Time"],
    flavor: "The second one. He built the first out of stone and did not like what it said about him, so he built this one out of light and has not liked that either.",
  },
  {
    id: "eyeland",
    name: "The Eyeland",
    subtitle: "Zone S",
    color: "#e11d48",
    hue: "#ff8fb4",
    brainElo: 2500,
    secretRegion: true,
    ilks: ["Dragon", "Time", "Phantom", "Space"],
    flavor: "An island that is an eye, or an eye that has an island around it. Six keepers stand on the ring and none of them will say which way round it is.",
  },
  {
    id: "dais",
    name: "The Crimson Dais",
    subtitle: "Zone Z",
    color: "#ff2266",
    hue: "#ff5c6b",
    brainElo: 2800,
    secretRegion: true,
    ilks: ["Dragon", "Time", "Phantom", "Space"],
    flavor: "A square of red stone at the centre of everything, hung in the dark with no road to it. Whatever it was cut from, the rest of it is gone.",
  },
];

export const REGION_BY_ID = Object.fromEntries(REGIONS.map(r => [r.id, r]));
export const regionHue = (id) => REGION_BY_ID[id]?.hue || "#e9edf6";

// ═══════════════════════════════════════════════════════════════
// THE CHILDREN
// ═══════════════════════════════════════════════════════════════
// Two Esclings. The Onboarding never names them, and naming a
// player's children is caP'n's call, not the engine's — so `name`
// is left null and the UI falls back to `label`. Fill the two
// strings whenever canon lands; nothing else has to move.
// NAMED 2026-08-18: Sun (male) + Moon (female), per decree. Elder=Sun, Younger=Moon paired off the accents (gold sun, cyan moon) — two-string swap if meant the other way.

// ── WHO THE PLAYER IS, IN A CUTSCENE ────────────────────────────
// Everywhere else on this map the player is their own Aenarchist name,
// because everywhere else they are a player. In a story scene they are
// a specific pair of parents who speak as "we" — that is canon (see
// TERROR_STORY_SPINE.md), and the authored lines are written for two
// mouths. A cutscene name-plate reading "Aenarchist" over the line
// "We declined the offer" is the seam showing.
export const STORY_PLAYER = "Lyraei & Lestre";

export const CHILDREN = [
  // ⚠ RE-HOMED 2026-08-21 with the rest of the map. Both nodes they
  // used to stand on stopped existing when caP'n re-traced the world,
  // and a CHILDREN row pointing at a dead id is the exact shape of the
  // 2026-08-18 bug where the trail was never stamped at all — silent,
  // and it takes the whole mythic ending with it.
  //
  // Placed by accent rather than by convenience: Sun is gold and is in
  // the north Boneyard where the candles are; Moon is cyan and is in
  // the still water of the west marsh, which is the one place in the
  // Minerium with a reflection in it.
  { id: "child_elder",   name: "Sun", label: "The Elder Escling",   node: "AFC-37", accent: "#e8b84b" },
  { id: "child_younger", name: "Moon", label: "The Younger Escling", node: "ABC-11", accent: "#22d3ee" },
];

export const CHILD_BY_ID = Object.fromEntries(CHILDREN.map(c => [c.id, c]));

// ⛔ THE TRAIL WAS NEVER STAMPED (found 2026-08-18, during the story
// pass). recordNodeClear() reads `node.child` and stamps `found/{id}`
// from it — and not one node in RAW_NODES has ever carried a `child`
// field. So `found` stayed empty forever, `childrenFound()` always
// returned nothing, and BOTH child-gated secrets — the Seal's and its
// neighbour's — were unreachable by construction. No test caught it:
// the requirement resolver is correct, the stamp is correct, and the
// two tables simply never pointed at each other.
//
// Fixed by deriving it, not by adding the field to two nodes: CHILDREN
// already declares where each child is, and that stays the one source
// of truth. Moving a child to another node is now one string.
export const CHILD_AT = Object.fromEntries(
  CHILDREN.filter(c => c.node).map(c => [c.node, c.id])
);
export const childAtNode = (nodeId) => CHILD_AT[nodeId] || null;
export const childName = (id) => {
  const c = CHILD_BY_ID[id];
  return c ? (c.name || c.label) : "???";
};

// ═══════════════════════════════════════════════════════════════
// NODES
// ═══════════════════════════════════════════════════════════════
// kind:
//   gate   — a region's first node
//   battle — an ordinary duel
//   fort   — the region's named boss; usually its exit
//   ghost  — a hidden node; only ever reached through a secret exit
//   vault  — a reward node (crests / a card), still a duel
//   warp   — a mouth of the Silver Lining
//   star   — a Silver Lining node
//   child  — one of your Esclings
//   throne — J'ynxi
//
// mx / my: ABSOLUTE pixels in MAP space (841 x 1264). These were
//   authored against the painted pads on `terror_ow_1.webp` — the
//   eyes in the Marsh, the gems in the Gardens, the pipe-valves in
//   the Graveyard, the stones in the Desert, the spiral ring on
//   the Eye. Use `world_pinner.html` to move one: it drags, it
//   snaps nothing, and it prints this exact block back out.
//
// boss: an id resolved against the EXISTING cast (Depths
//   gatekeepers, Inner Minerium villains, story opponents). We
//   never re-type a character that already exists somewhere —
//   one source of truth, see resolveBoss().
//
// secretReq: what must be true of what you BRING for this node's
//   second exit to exist. Checked before the duel, not during it —
//   which is exactly caP'n's ask ("secrets based on beings you're
//   using") and needs zero match instrumentation. A `result`-based
//   secret (flawless, under N turns) is a later pass; the seam is
//   `secretReq.result`, deliberately unread for now.

// ═══════════════════════════════════════════════════════════════
// THE GEOMETRY — the two tables the pinner owns
// ═══════════════════════════════════════════════════════════════
// EVERY coordinate in this file lives in these two objects and
// nowhere else. That separation is the whole reason re-pinning the
// world is a paste instead of a session.
//
// The alternative — mx/my written inline on each of the 46 node
// objects — was the first cut, and it is worse for one specific
// reason: a node object is PROSE. It carries a name, a flavour
// line, a boss id, a secret requirement. Coordinates buried in that
// prose cannot be regenerated by a tool without the tool having to
// parse and rewrite the prose around them, which means the tool
// would eventually eat someone's flavour text. Here it rewrites two
// self-contained blocks and can touch nothing else.
//
// PIN  — node id → [mx, my] in MAP pixels (841 x 1264).
//        Authored against the painted pads on `terror_ow_1.webp`:
//        the eyes in the Marsh, the gems in the Gardens, the
//        pipe-valves in the Graveyard, the stones in the Desert,
//        the spiral ring on the Eye.
//
// BEND — edge key ("from>to") → waypoints the road bows through.
//        Absent means a straight line, which is almost every road.
//
// TO RE-PIN: open `world_pinner.html`, drag, press COPY, and paste
// over these two blocks. Nothing else in this file moves. A node
// missing from PIN does not crash — it lands dead centre of the
// map with a console warning, which is loud, harmless, and
// impossible to miss in playtest.

// ═══════════════════════════════════════════════════════════════
// THE PINS — read off caP'n's traced overlay, 2026-08-21
// ═══════════════════════════════════════════════════════════════
// Every pair is [mx, my] in the painting's own 841×1264 space, taken
// from the hand-traced map caP'n drew the zones and the road onto.
//
// ⚠ THESE ARE A TRACE, NOT A MEASUREMENT, and they are meant to be
// dragged. `world_pinner.html` is the instrument: open it, move any
// pin that sits off its painted pad, copy the block it prints, paste
// it over this one. That loop is the intended workflow and it is why
// this table is a flat literal with nothing clever in it.
const PIN = {
  // ── S · THE EYELAND ─────────────────────────────────────────
  "ASS-48":  [ 172,   52 ],   // N  — the Upper Lid
  "ASS-47":  [ 265,   88 ],   // NE — the White
  "ASS-46":  [ 258,  172 ],   // SE — the Outer Ring
  "ASS-45":  [ 180,  206 ],   // S  — the Tear Duct
  "ASS-44":  [  95,  172 ],   // SW — the Lower Lid
  "ASS-49":  [  92,   90 ],   // NW — the Blind Spot
  "ASS-50":  [ 172,  128 ],   // the pupil itself

  // ── Z · THE MOON ────────────────────────────────────────────
  // Not a node. A button, pinned so the chrome knows where the
  // painting keeps it. See THE MOON.
  "AZT-51":  [  30,   22 ],

  // ── G · SHV's CRYSTAL MAUSOLEUM ─────────────────────────────
  "AGL-43":  [ 408,  332 ],
  "AGS-42":  [ 370,  378 ],
  "AGS-41":  [ 455,  378 ],
  "AGS-40":  [ 540,  337 ],

  // ── F · BONEYARD NORTH ──────────────────────────────────────
  "AFS-39":  [ 660,  350 ],
  "AFC-38":  [ 722,  382 ],
  "AFC-37":  [ 690,  455 ],
  "AFC-36":  [ 718,  490 ],
  "AFC-35":  [ 688,  562 ],

  // ── E · DESERTED DESERT ─────────────────────────────────────
  "AES-34":  [ 672,  688 ],
  "AES-32":  [ 640,  726 ],
  "AEL-33":  [ 742,  722 ],
  "AEC-31":  [ 712,  776 ],
  "AEC-30":  [ 657,  776 ],
  "AEC-29":  [ 655,  812 ],
  "AEC-28":  [ 600,  828 ],

  // ── Z · THE CRIMSON DAIS ────────────────────────────────────
  "AZS-55":  [ 432,  570 ],   // N
  "AZS-52":  [ 348,  638 ],   // W — where the Moon puts you
  "AZS-56":  [ 432,  636 ],   // the centre. The end of the story.
  "AZS-54":  [ 516,  638 ],   // E
  "AZS-53":  [ 432,  708 ],   // S

  // ── B · HARSH MARSH WEST ────────────────────────────────────
  "ABL-13":  [ 163,  520 ],
  "ABS-14":  [ 228,  530 ],
  "ABC-12":  [ 196,  556 ],
  "ABC-11":  [ 178,  622 ],
  "ABC-10":  [ 152,  696 ],

  // ── A · BONEYARD SOUTH ──────────────────────────────────────
  "AAC-09":  [ 150,  745 ],
  "AAC-07":  [ 108,  772 ],
  "AAC-08":  [ 206,  772 ],
  "AAC-05":  [ 148,  896 ],
  "AAC-06":  [ 206,  896 ],
  "AAS-04":  [ 160,  962 ],
  "AAC-15":  [ 210,  990 ],
  "AAC-03":  [ 130, 1000 ],
  "AAL-16":  [ 178, 1052 ],
  "AAC-02":  [ 112, 1064 ],
  "AAC-01":  [ 128, 1100 ],   // THE START

  // ── C · SHV's MAUSOLEUM ─────────────────────────────────────
  "ACC-27":  [ 548,  940 ],
  "ACC-25":  [ 535, 1030 ],
  "ACC-17":  [ 348, 1030 ],
  "ACL-26":  [ 438, 1050 ],
  "ACC-22":  [ 583, 1085 ],
  "ACC-23":  [ 625, 1112 ],
  "ACC-18":  [ 360, 1112 ],
  "ACS-19":  [ 475, 1112 ],
  "ACC-20":  [ 440, 1148 ],
  "ACC-21":  [ 525, 1148 ],

  // ── D · HARSH MARSH EAST ────────────────────────────────────
  "ADL-24":  [ 700, 1085 ],
};

const BEND = {
  // ⚠ EMPTIED 2026-08-21. Every entry in here was a waypoint list for
  // a road on the OLD 91-row machine-traced graph, keyed by the OLD node
  // ids — so after caP'n re-traced the map by hand not one of them
  // addressed a road that still exists. A bend table that keys nothing
  // is worse than an empty one: `edgeKey` finds no match, the road
  // silently draws straight, and the file goes on claiming it curves.
  //
  // The mechanism is untouched and one row brings it back. The format is
  // `"FROM>TO": [[mx,my], …]` and the coordinates are in the painting's
  // own space, same as PIN. Trace them off the yellow ink whenever a road
  // is drawn bowing round something.
};

// ═══════════════════════════════════════════════════════════════
// THE NODES — 55 stops and one button, caP'n's numbering
// ═══════════════════════════════════════════════════════════════
// Re-authored 2026-08-21 against the hand-traced overlay. Numbers 1
// through 56 are caP'n's; 51 is THE MOON and is not a fight, which is
// why there are 55 playable nodes and 56 numbers.
//
// ── EVERY NODE FIELDS A NAMED PERSON NOW ────────────────────────
// caP'n: *"you can create names and stories etc for all the node
// opponents and Sol King, Lord's Chambers, all of it. I will review
// it after."* So `foe` is a new field and it is on every row:
//
//   foe: { name, title?, ilk, deck, npcSkill?, lore }
//
// Before this, an unbossed node called `generateOpponent()` and got a
// random name off the Depths list with a random starter deck — which
// is fine for an endless ladder and wrong for a story. `opponentForNode`
// reads `foe` first now; `generateOpponent` is the fallback for a node
// nobody has written yet, and there are none left in this Minerium.
//
// ⭐ THE ILK LAW IS PER ZONE, AND IT IS caP'n's, VERBATIM:
//   *"Zones A and F will focus on Dragon and Time decks… Zones C and E
//    will use Time and Phantom. Zones B and D will use Phantom and
//    Space. Zone G will use Space and Time. Zone S will use all
//    current 4 Ilk… Zone Z will use all current 4 as well, just give
//    final boss Ant deck."*
// It is a law for continuity, not a law of the universe — a species
// does not have to fight with its own Ilk, and several here do not.
// The ZONE is where the cohesion lives.
//
// ⚠ AND THE DRAGON CAP IS THE HARDER LAW: *"never have more than 3
// Dragon NPC's (4 if boss is included) in one single Minerium — it
// makes them feel less rare."* There are exactly three, and they are
// deliberately spread so no two are met in one sitting:
//   · AAS-04 · Ossuar, the Rib-Warden      (Zone A, the first Sol King)
//   · AFS-39 · Bael, the Long Winter        (Zone F)
//   · AZS-55 · Ythra, the Last Clutch       (Zone Z, the Brood Gallery)
// Zone S therefore runs Phantom, Space and Time across its seven, and
// gets its fourth Ilk from the Dais rather than from a fourth dragon.
// Every one of these is one field to move if caP'n wants them
// arranged differently.
const RAW_NODES = [

  // ══════════════════════════════════════════════════════════════
  // ZONE A · BONEYARD SOUTH — Dragon and Time
  // ══════════════════════════════════════════════════════════════
  // Where the road out of the Plaza gives up. The gentlest zone in the
  // world and the only one a brand-new deck has any business in.
  {
    id: "AAC-01", region: "boneyard_south", mark: "cursed", name: "The Roadhead", kind: "gate",
    difficulty: "Beb", start: true,
    flavor: "Where the Plaza road stops pretending to be a road. Somebody has been sleeping here, badly, on top of somebody else.",
    foe: {
      name: "Wretch", ilk: "Time", deck: "starter_z4z", species: "Z4Z", npcSkill: 0.18,
      lore: "Not a guard. Not a bandit. A Z4Z that lay down at the edge of the boneyard some years ago and has been getting up ever since, out of habit, to see who is passing. Whatever it was sent to do, it finished, and nobody came to say so.",
    },
    // The gentlest riddle in the world, because it is the FIRST node
    // and its egg has to be answerable by a starter deck and nothing
    // else. Never make this one harder.
    hint: "Whoever slept here bedded down facing the stars and left without their fire. Come back carrying something that walks between them.",
    secretReq: { cls: "Space" },
    secretFlavor: "The bedroll is still warm. It was never theirs.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AAC-02", region: "boneyard_south", mark: "cursed", name: "The Kettle Steps", kind: "battle",
    difficulty: "Beb",
    flavor: "Nine stairs cut into a hill that is not a hill. Something has been boiling at the top of them for longer than the stairs have existed.",
    foe: {
      name: "Kettle", ilk: "Time", deck: "starter_dg0", species: "DG-0", npcSkill: 0.2,
      lore: "She tends the pot. Nobody has ever established what is in the pot. She is pleasant about it, which is the part that gets people.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "She has been offering the pot for a very long time and nobody has ever declined for a good reason. Bring something that has no mouth.",
    secretReq: { ilk: "Machine" },
    secretFlavor: "It looks into the pot, reports nothing, and she laughs for the first time in an age.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AAC-03", region: "boneyard_south", mark: "cursed", name: "The Two-Spade Field", kind: "battle",
    difficulty: "Beb",
    flavor: "Turned earth in rows, and the rows are the wrong width for anything that grows.",
    foe: {
      name: "Mally Two-Spades", ilk: "Time", deck: "starter_z4z", species: "Z4Z", npcSkill: 0.24,
      lore: "Digs with one and fills with the other and has never once used them in the wrong order. Says the second spade is the important one and will not be drawn on why.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "The rows are the wrong width for anything that grows. They are the right width for something that has not happened yet — bring something that has already been there.",
    secretReq: { cls: "Time" },
    secretFlavor: "The second spade goes down first. He watches you notice, and says nothing, which from him is a speech.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AAS-04", region: "boneyard_south", mark: "shard", name: "The Ribcage", kind: "fort",
    difficulty: "Champion", shards: 2,
    // ⭐ A TROPHY COMPANION, AND IT IS A FIELD NOW RATHER THAN A BOSS.
    // `STORY_TROPHY_BY_BOSS` keys off `node.boss`, and the re-trace
    // left exactly ONE node in the world carrying a boss — so all four
    // story companions, including the game's only mythic, went
    // unobtainable again, which is the identical bug fixed on
    // 2026-08-20. Decoupling it means a node can field its own written
    // opponent AND still owe a trophy.
    trophy: "suhnker_trophy",
    flavor: "Something enormous came down here and the bones stayed up. There is weather inside it that is not the weather outside it.",
    // ⭐ DRAGON 1 OF 3. See the cap above.
    foe: {
      name: "Ossuar", title: "the Rib-Warden", ilk: "Dragon", deck: "starter_lucky_dragon", species: "Lucky Dragon", npcSkill: 0.4,
      lore: "A dragon who found a dead one's ribs and moved in rather than build. The Minerium's first Sol King, and the only one who came by the post honestly — everybody else here was appointed by someone who has since left.",
    },
    hint: "It kept the bones and it kept the weather. It will not open its hand to anything that has not also kept something. Bring a being that has been through a level and remembers it.",
    // ⚠ `natural3: false` WAS HERE AND MEANT NOTHING. `meetsReq` reads
    // `if (req.natural3 && !ctx.natural3)`, so a false value is
    // indistinguishable from an absent key — there is no
    // "explicitly NOT a Natural Lord" primitive and this row was
    // silently just `{ cls: "Dragon" }`. Said out loud rather than
    // left looking authored.
    secretReq: { ilk: "Dragon" },
    secretFlavor: "It moves one rib aside. It is a door, and it always was.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AAC-05", region: "boneyard_south", mark: "cursed", name: "The Low Cairn", kind: "battle",
    difficulty: "Beb",
    flavor: "Stones piled to knee height in a place with no stones. Somebody carried every one of them a long way to make something this small.",
    foe: {
      name: "Pim", ilk: "Time", deck: "starter_dg0", species: "DG-0", npcSkill: 0.26,
      lore: "Adds one stone a day and has never explained the count. The cairn has been knee height for as long as anyone remembers, so either the days are not adding up or somebody else is taking them off.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Every stone here was carried a long way to build something very small. Bring somebody who understands carrying.",
    secretReq: { ilk: "Humanoid" },
    secretFlavor: "He adds your stone to the pile. The pile is still knee height. Neither of you mentions it.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AAC-06", region: "boneyard_south", mark: "cursed", name: "The Sold Ground", kind: "battle",
    difficulty: "Beb",
    flavor: "Plots, staked and numbered, in a boneyard that has never had an owner. The numbers go up to four hundred and the stakes are all new.",
    foe: {
      name: "Bartleby Nix", ilk: "Time", deck: "starter_antdroid", species: "Antdroid", npcSkill: 0.3,
      lore: "Sells plots. Has sold this ground eleven times to eleven people, none of whom have arrived, all of whom paid. He keeps the receipts in beautiful order and will show you them if you ask.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "He has sold this ground eleven times and kept every receipt. He will not be beaten by a better argument — only by a better piece of paper.",
    secretReq: { owns: "tome_devils_advocate" },
    secretFlavor: "He reads your paper twice, files it beautifully, and sells you nothing. It is the highest compliment he has.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AAC-07", region: "boneyard_south", mark: "cursed", name: "The Widow's Rest", kind: "battle",
    difficulty: "Beb",
    flavor: "A chair, a lamp and a clean-swept square of dirt, three days' walk from anything that would need a chair.",
    foe: {
      name: "The Gravedigger's Widow", ilk: "Time", deck: "starter_z4z", species: "Z4Z", npcSkill: 0.32,
      lore: "Her husband dug this whole yard and is not in it. She has been waiting where he told her to wait, and she has got very good at the waiting, and she has got very bad at being interrupted during it.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "She has been waiting where she was told to wait, and she has got very bad at being interrupted. Arrive with somebody who is also still here.",
    secretReq: { cls: "Phantom" },
    secretFlavor: "She does not get up. She moves the lamp, very slightly, so it falls on both of you.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AAC-08", region: "boneyard_south", mark: "cursed", name: "The Rookery", kind: "battle",
    difficulty: "Beb",
    flavor: "Every skull on this side of the field is facing the same way, and none of them are facing the road.",
    foe: {
      name: "Corvish", ilk: "Time", deck: "starter_dg0", species: "DG-0", npcSkill: 0.34,
      lore: "Turns them. All of them, every morning, to whatever it is they are supposed to be looking at. Refuses to look at it himself, which he says is the difference between a job and a calling.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Every skull faces the same way and none of them face the road. Bring something that already knows what a bird is for.",
    secretReq: { ilk: "Beast" },
    secretFlavor: "The skulls turn. All of them, at once, and Corvish finally looks at what they were looking at.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AAC-09", region: "boneyard_south", mark: "cursed", name: "The Counting House", kind: "battle",
    difficulty: "Champion",
    flavor: "A shack with a ledger in it that is older than the shack, and a window that looks north into the marsh.",
    foe: {
      name: "Hob the Counter", ilk: "Time", deck: "starter_antdroid", species: "Antdroid", npcSkill: 0.42,
      lore: "Counts everything that goes north and nothing that comes back, which he insists is not a grim job because the column has been empty a long time and an empty column is just an empty column.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "He counts everything that goes north and nothing that comes back, and says an empty column is just an empty column. Go east, come back, and give him something to write.",
    secretReq: { cleared: "AAC-15" },
    secretFlavor: "He opens the second column. His hand shakes. He has been waiting a long time to be wrong about this.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AAC-15", region: "boneyard_south", mark: "cursed", name: "The Long Gate", kind: "battle",
    difficulty: "Beb",
    flavor: "A gate with no fence attached to it, standing where the yard stops being a yard and starts being a road east.",
    foe: {
      name: "Sorrel Quick", ilk: "Time", deck: "starter_z4z", species: "Z4Z", npcSkill: 0.3,
      lore: "Opens it for people. There is nothing on either side of it. She has never once let anyone through without opening it first and she does not think that is funny.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "A gate with no fence attached to it. She has never let anyone through without opening it first. Bring the kind that was never going to use the gate.",
    secretReq: { cls: "Space" },
    secretFlavor: "She opens it anyway. You go through anyway. Both of you understand that this was the correct thing to do.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AAL-16", region: "boneyard_south", mark: "chamber", name: "The Bone Chamber", kind: "vault",
    difficulty: "Champion",
    flavor: "An arch of stacked femurs with a green flame in its mouth. It is the only warm thing in the south yard and nothing nests near it.",
    foe: {
      name: "The Bone Steward", ilk: "Time", deck: "starter_antdroid", species: "Antdroid", npcSkill: 0.48,
      lore: "One of six. They do not speak to each other, no two of them are the same species, and every one of them is standing in front of a green flame in a different corner of this Minerium waiting for someone who is not coming back.",
    },
    hint: "The flame in its mouth has been burning without fuel since before the yard was a yard. It will not answer to anything that has not also outlasted its own beginning. Bring something that has already died once and come back.",
    secretReq: { cls: "Phantom" },
    secretFlavor: "The flame leans toward the one thing here it recognises.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },

  // ══════════════════════════════════════════════════════════════
  // ZONE B · HARSH MARSH WEST — Phantom and Space
  // ══════════════════════════════════════════════════════════════
  // A cul-de-sac off the top of the Boneyard, and deliberately so: it
  // holds a Sol King and a Lord's Chamber and NOTHING leads out of it,
  // so a player who walks it walks back out the way they came. The
  // reward for going is a beacon into the Eyeland.
  {
    id: "ABC-10", region: "marsh_west", mark: "cursed", name: "The Wading Line", kind: "battle",
    difficulty: "Beb",
    flavor: "Where the ground stops being ground. There is a line of stones under the water and it is not straight, and following it is the only way across that does not involve swimming.",
    foe: {
      name: "Wade", ilk: "Phantom", deck: "starter_spectral_tiger", species: "Spectral Tiger", npcSkill: 0.36,
      lore: "Knows where every stone is. Charges nothing. Has never told anyone where the stones are, and has never once let anyone drown, and does not consider those two facts to be in tension.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "There is a line of stones under the water and it is the only way across. Arrive with something that did not need it.",
    secretReq: { ilk: "Dragon" },
    secretFlavor: "Wade watches you come over the water instead of through it, and marks the stones off in his head one by one.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "ABC-11", region: "marsh_west", mark: "cursed", name: "The Still Reach", kind: "battle",
    difficulty: "Beb",
    flavor: "Water so flat it has stopped being water and started being a floor. The reflection is a half-second behind, and only in this one place.",
    foe: {
      name: "Mirelle", ilk: "Space", deck: "starter_h09", species: "H-09", npcSkill: 0.4,
      lore: "Lives on the far bank and has never crossed. Says the reflection has been getting further behind for years and that when it catches up she will finally know which of them has been doing the waiting.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "The reflection is half a second behind everywhere except the one place it is waiting. Bring something that has crossed farther than this water is wide.",
    secretReq: { cls: "Space" },
    secretFlavor: "For one full second the reflection is exactly on time, and Mirelle stops pretending she is not impressed.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "ABC-12", region: "marsh_west", mark: "cursed", name: "The Drowned Choir", kind: "battle",
    difficulty: "Champion",
    flavor: "Nine trees standing in a ring in the deep water, dead, bark stripped to the same height on every one of them. When the wind is right they are almost tuneful.",
    foe: {
      name: "Anthem", title: "Who Kept the Ninth Part", ilk: "Phantom", deck: "starter_inverted_serpent", species: "Inverted Serpent", npcSkill: 0.5,
      lore: "There were ten. She is the reason there are nine, and she is the reason there are still nine, and she will tell you that those were two separate decisions made a long way apart.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Nine dead trees, stripped to the same height, almost a tune when the wind is right. Bring the tenth voice — and it had better already sound like them.",
    secretReq: { all: [{ ilk: "Beast" }, { cls: "Phantom" }] },
    secretFlavor: "It joins in. The chord closes. Nine trees have been a ninth of a chord for a very long time.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "ABL-13", region: "marsh_west", mark: "chamber", name: "The Still Chamber", kind: "vault",
    difficulty: "Champion",
    flavor: "An arch half-sunk in the shallows with a green flame standing on the water in its mouth. The water does not put it out and the flame does not dry the water.",
    foe: {
      name: "Fen", title: "Keeper of the Still Water", ilk: "Space", deck: "starter_xulan", species: "Xulan", npcSkill: 0.54,
      lore: "The second of the six Stewards, and the only one who sat down. He says the others are all still standing and that this is why they will break first, and he has been proved wrong about that four times and does not appear to have noticed.",
    },
    hint: "The flame stands on water and neither one gives. It wants something that has learned the same trick — a thing that goes where it should not go and is not diminished. Bring what walks between the stars.",
    secretReq: { cls: "Space" },
    secretFlavor: "The flame steps off the water and onto your hand, and it is not hot.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "ABS-14", region: "marsh_west", mark: "shard", name: "The Green Tide", kind: "fort",
    difficulty: "Champion", shards: 2,
    flavor: "The one place the marsh moves. It comes in twice a day, and it is not water that comes in.",
    foe: {
      name: "Gollet", title: "the Green Tide", ilk: "Phantom", deck: "starter_shadow_fox", species: "Shadow Fox", npcSkill: 0.52,
      lore: "A Sol King who does not hold a shard so much as be one, slowly, in the shape of a fox that has stopped bothering to look like one. Twice a day he floods this end of the marsh and twice a day he pulls it back, and he does not know why he does it either.",
    },
    hint: "It comes in twice a day and it is not water. It will not be looked at by anything solid. Bring a being the tide can pass through.",
    secretReq: { cls: "Phantom" },
    secretFlavor: "The tide comes in and goes straight through the two of you, and takes nothing.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },

  // ══════════════════════════════════════════════════════════════
  // ZONE C · SHV's MAUSOLEUM — Time and Phantom
  // ══════════════════════════════════════════════════════════════
  // The black spires, and the largest zone in the world. He built it
  // for himself before he had anything worth burying, and the staff
  // have kept it immaculate for a man who has not died.
  {
    id: "ACC-17", region: "mausoleum", mark: "cursed", name: "The West Approach", kind: "battle",
    difficulty: "Champion",
    flavor: "A processional road wide enough for a hundred and worn down the middle by one.",
    foe: {
      name: "Sergeant Pell", ilk: "Time", deck: "starter_dg0", species: "DG-0", npcSkill: 0.46,
      lore: "Marshal Malone's, originally. Left behind here when the recruiting party moved on and has held the approach for eleven years on the assumption that being forgotten and being trusted look identical from the inside.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "A road wide enough for a hundred, worn down the middle by one. Sergeant Pell has been that one since before the wearing started. Bring something that marches.",
    secretReq: { ilk: "Machine" },
    secretFlavor: "He falls in beside you without being asked, walks eleven paces, and stops. It is the whole parade he has left.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "ACC-18", region: "mausoleum", mark: "cursed", name: "The Quillon Steps", kind: "battle",
    difficulty: "Champion",
    flavor: "Stairs going down into the plinth of a building that has no down. They arrive.",
    foe: {
      name: "Quillon", ilk: "Phantom", deck: "starter_spectral_tiger", species: "Spectral Tiger", npcSkill: 0.5,
      lore: "Guards the stairs from the bottom, facing up. Has never been relieved. Points out that a guard facing the wrong way is either a fool or has been told something about which direction the danger is coming from.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "The stairs go down into a building that has no down. They arrive anyway. Bring something that also arrives where it should not.",
    secretReq: { cls: "Phantom" },
    secretFlavor: "You reach the bottom twice. Quillon has the courtesy not to comment on the first time.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "ACS-19", region: "mausoleum", mark: "shard", name: "The Tally Vault", kind: "fort",
    difficulty: "Champion", shards: 2,
    flavor: "A room of shelves, floor to ceiling, and every shelf holds one small stone with one small number scratched on it.",
    foe: {
      name: "Vaunt", title: "the Tally-Keeper", ilk: "Time", deck: "starter_antdroid", species: "Antdroid", npcSkill: 0.56,
      lore: "Counts what SHV owes and what SHV is owed, in two columns, on two walls. The owing wall is full to the ceiling. The owed wall has one stone on it and he will not say whose name is on the other side of it.",
    },
    hint: "Two walls, and one of them has a single stone on it. He will not open the vault to anyone who has not also carried a debt they cannot put down. Bring something that has been through every level and still owes the first one.",
    secretReq: { natural3: true },
    secretFlavor: "He turns the one stone over. There is no name on it. There never was.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "ACC-20", region: "mausoleum", mark: "cursed", name: "The Ash Gallery", kind: "battle",
    difficulty: "Champion",
    flavor: "Portraits, hundreds of them, and every face has been carefully and completely burned out of the canvas without damaging the frame.",
    foe: {
      name: "Ashe Meridian", ilk: "Phantom", deck: "starter_shadow_fox", species: "Shadow Fox", npcSkill: 0.52,
      lore: "The restorer. Has been repairing the frames for nine years and has never touched a canvas. Says the frames are the commission and the faces are somebody else's business, and she is not being coy — nobody has ever given her the second half of the job.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Hundreds of portraits and every face burned out without touching the frame. Bring a face worth the trouble.",
    secretReq: { natural3: true },
    secretFlavor: "Ashe Meridian looks at what you brought, and for the first time in this gallery, reaches for a frame instead of a flame.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "ACC-21", region: "mausoleum", mark: "cursed", name: "The Sealed Wing", kind: "battle",
    difficulty: "Champion",
    flavor: "A corridor bricked up at both ends with a door in the middle, and the brickwork is newer than the door.",
    foe: {
      name: "Grissom", ilk: "Time", deck: "starter_z4z", species: "Z4Z", npcSkill: 0.54,
      lore: "Laid both walls himself, from the inside, which is a thing that can only be done once and only by someone who has thought about it. He is perfectly content and considers the door decorative.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "A corridor bricked at both ends with a door in the middle, and the brickwork is newer than the door. Nobody sealed you in. Somebody sealed something out. Come at it from the far side.",
    secretReq: { cleared: "ACC-22" },
    secretFlavor: "The door opens the way it was hung to open, which is the direction Grissom has never once tried.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "ACC-22", region: "mausoleum", mark: "cursed", name: "The Reliquary", kind: "battle",
    difficulty: "Champion",
    flavor: "Cases of relics in a building where nobody is buried. Every label is a name and a date and the dates are all in the future.",
    foe: {
      name: "The Reliquary Twins", ilk: "Phantom", deck: "starter_inverted_serpent", species: "Inverted Serpent", npcSkill: 0.58,
      lore: "One writes the labels and one dusts the cases and they have never been seen in the same room, which the staff find funny and which is not a joke.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Every label is a name and a date, and every date is in the future. Bring something that has already read this room.",
    secretReq: { cls: "Time" },
    secretFlavor: "The Twins check a label, then check you, then very carefully turn the card face down.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "ACC-23", region: "mausoleum", mark: "cursed", name: "The Coffin Shop", kind: "battle",
    difficulty: "Champion",
    flavor: "A workshop with one commission in it, unfinished, and it has been unfinished long enough for the sawdust to have gone to stone.",
    foe: {
      name: "Coffin-Wright Odd", ilk: "Time", deck: "starter_dg0", species: "DG-0", npcSkill: 0.56,
      lore: "Building SHV's, to measure, since the year he arrived. Adds a plank when the mood is right. Has calculated that at his present rate he will finish it a comfortable distance after everyone involved has stopped caring, which he regards as the correct pace for the work.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "One commission, unfinished long enough for the sawdust to go to stone. It was never going to fit a person. Bring what it was measured for.",
    secretReq: { all: [{ ilk: "Machine" }, { natural3: true }] },
    secretFlavor: "Odd takes one look, picks the plane back up, and does not speak again until the lid is on.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "ACC-25", region: "mausoleum", mark: "cursed", name: "The East Cloister", kind: "battle",
    difficulty: "Champion",
    flavor: "An open square of columns with the sky in the middle of it. Everything else in this building has a roof.",
    foe: {
      name: "Lisbet Crowe", ilk: "Phantom", deck: "starter_spectral_tiger", species: "Spectral Tiger", npcSkill: 0.58,
      lore: "Sweeps the cloister. There is nothing to sweep — it is open to the sky and the sky here drops nothing. She has swept it every day for six years and the day she stops is a day somebody should be worried about.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Everything in this building has a roof except this square. Somebody left it open on purpose. Bring the ones who would use it.",
    secretReq: { cls: "Space" },
    secretFlavor: "Lisbet Crowe looks up, which nobody in this cloister has done in living memory.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "ACL-26", region: "mausoleum", mark: "chamber", name: "The Black Chamber", kind: "vault",
    difficulty: "Lord",
    flavor: "The heart of the spires, and the green flame in it is the only colour in a building made entirely of two.",
    foe: {
      name: "The Mausoleum Chamberlain", ilk: "Time", deck: "starter_dg0", species: "DG-0", npcSkill: 0.66,
      lore: "The third Steward, and the one who runs a household. Two hundred staff, immaculate books, a full roster for a building nobody enters — and not one line item anywhere for what the green flame is for.",
    },
    hint: "Two hundred staff, immaculate books, and no line item for the flame. He will open the chamber for something the ledger cannot account for either. Come with a Natural Lord standing and a Phantom beside it.",
    secretReq: { all: [{ natural3: true }, { cls: "Phantom" }] },
    secretFlavor: "He writes one line. It is the first entry in nine years and he does not read it back.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "ACC-27", region: "mausoleum", mark: "cursed", name: "The North Stair", kind: "battle",
    difficulty: "Champion",
    flavor: "The way out and up, toward the desert. The steps are cut for something with a longer stride than a person.",
    foe: {
      name: "Halloway", ilk: "Phantom", deck: "starter_shadow_fox", species: "Shadow Fox", npcSkill: 0.6,
      lore: "Counts everyone going up. Has never counted anyone coming down, and has stopped saying so out loud because of how people take it.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "The steps are cut for a longer stride than a person has. Somebody built this stair for a guest who never came. Bring the guest.",
    secretReq: { ilk: "Dragon" },
    secretFlavor: "It takes the stair three at a time, the way the stair was always asking to be taken, and Halloway steps aside.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },

  // ══════════════════════════════════════════════════════════════
  // ZONE D · HARSH MARSH EAST — Phantom and Space
  // ══════════════════════════════════════════════════════════════
  // One node. One arch, one keeper, and a beacon into the Eyeland at
  // the end of it — the smallest zone in the world and the shortest
  // detour to a permanent door.
  {
    id: "ADL-24", region: "marsh_east", mark: "chamber", name: "The Waiting Chamber", kind: "vault",
    difficulty: "Champion",
    flavor: "An arch on a spit of land with water on three sides and a green flame in its mouth, and one chair set out facing the road.",
    foe: {
      name: "Sedge", title: "Who Waited", ilk: "Space", deck: "starter_h01", species: "H-01", npcSkill: 0.6,
      lore: "The fourth Steward. Put the chair out on the first day for whoever was coming, and has never sat in it, because it is not hers. It has not rotted. Nothing on this spit has rotted.",
    },
    hint: "The chair has been set out for somebody for a very long time and it has not rotted, and neither has she. Bring a being that also refuses to be finished — something that walks between the stars.",
    secretReq: { cls: "Space" },
    secretFlavor: "She sits down. It is the first time, and she gets straight back up.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },

  // ══════════════════════════════════════════════════════════════
  // ZONE E · DESERTED DESERT — Time and Phantom
  // ══════════════════════════════════════════════════════════════
  // Sand with amethyst coming up through it like a crop. Deserted
  // twice: once by whoever lived here and once by the hour.
  {
    id: "AEC-28", region: "desert", mark: "cursed", name: "The First Dune", kind: "battle",
    difficulty: "Champion",
    flavor: "The sand starts exactly here, in a line, and there is nothing on the other side of the line for it to have come from.",
    foe: {
      name: "Dune-Walker Tam", ilk: "Time", deck: "starter_z4z", species: "Z4Z", npcSkill: 0.56,
      lore: "Walks the line. Says the sand is arriving rather than spreading, which is a different thing and a worse one, and that he has been keeping the count of how much for nineteen years and has told nobody the number.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "The sand starts exactly here, in a line, and there is nothing on the far side for it to have come from. Bring something that can tell you when it started.",
    secretReq: { cls: "Time" },
    secretFlavor: "Tam walks the line with you and, at the eleventh step, admits he has never once crossed it going the other way.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AEC-29", region: "desert", mark: "cursed", name: "The Glassworks", kind: "battle",
    difficulty: "Champion",
    flavor: "Sand fused into sheets, standing upright in rows, and every sheet is clear enough to read a book through.",
    foe: {
      name: "The Glasswright", ilk: "Phantom", deck: "starter_inverted_serpent", species: "Inverted Serpent", npcSkill: 0.6,
      lore: "Makes the sheets. Will not say what the heat comes from. Has made two thousand of them and has never once made a window, because a window needs a wall and she has been very careful never to build one.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Every sheet is clear enough to read a book through, and there is nothing behind any of them. Bring something worth looking at through glass.",
    secretReq: { all: [{ cls: "Phantom" }, { natural3: true }] },
    secretFlavor: "The Glasswright turns a sheet toward you and, for a moment, all the sheets show the same thing.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AEC-30", region: "desert", mark: "cursed", name: "The Sunk Market", kind: "battle",
    difficulty: "Champion",
    flavor: "Stalls, awnings and a well, all of it up to the shoulders in sand, all of it still stocked.",
    foe: {
      name: "Osgood Rell", ilk: "Time", deck: "starter_dg0", species: "DG-0", npcSkill: 0.6,
      lore: "The last trader. Keeps the stall above the sand by digging it out every morning, keeps the prices where they were the day the town left, and has not sold anything in a decade because he will not sell to anyone who does not haggle and nobody has the heart.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Stalls up to the shoulders in sand and every one of them still stocked. Rell has not sold anything in an age. Bring him a customer.",
    secretReq: { ilk: "Humanoid" },
    secretFlavor: "He takes payment in something that is not money and, for the length of one transaction, this is a market again.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AEC-31", region: "desert", mark: "cursed", name: "The Marrow Vein", kind: "battle",
    difficulty: "Champion",
    flavor: "A seam of amethyst breaking the surface and running east, and the crystal in it is warm.",
    foe: {
      name: "Marrow", ilk: "Phantom", deck: "starter_shadow_fox", species: "Shadow Fox", npcSkill: 0.62,
      lore: "Follows the seam. Says it is going somewhere and that the somewhere is what matters, and has been following it for so long that he has gone round the whole desert twice without once noticing the seam is a ring.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "The seam is warm, and it runs east toward everything that is still holding a Sol. Bring proof you have taken some already.",
    // ⚠ SIX IS EXACTLY WHAT IS EARNABLE BEFORE THIS NODE, WITH NO
    // SLACK: AAS-04 (2) + ABS-14 (2) + ACS-19 (2). Re-price any of
    // those three, or move one behind this node, and this egg bricks
    // silently — a riddle nobody can answer looks identical to a
    // riddle nobody has answered. Raise the number only after
    // re-counting the reachable keepers.
    secretReq: { shardsEarned: 6 },
    secretFlavor: "The amethyst goes cold as you pass. Marrow says this happens to everybody eventually, and is plainly lying.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AES-32", region: "desert", mark: "shard", name: "The Stopped Hour", kind: "fort",
    difficulty: "Lord", shards: 2,
    flavor: "A courtyard of sundials, all of them casting the same shadow, and none of them casting it in the direction of any light that is here.",
    foe: {
      name: "Kesst", title: "the Hour-Thief", ilk: "Time", deck: "starter_antdroid", species: "Antdroid", npcSkill: 0.7,
      lore: "A Sol King who took the hour off this place and has been carrying it around ever since, and has discovered what everyone who takes something like that discovers: that he cannot put it down and he cannot use it and he cannot give it away.",
    },
    hint: "Every shadow here points the same way and no light here points that way. He will not give up the hour to anything that has not also gone somewhere it should not have. Bring something that steps between the stars, and a Natural Lord to carry it.",
    secretReq: { all: [{ cls: "Space" }, { natural3: true }] },
    secretFlavor: "Every sundial moves at once. They are all wrong in a new direction now, which is progress.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AEL-33", region: "desert", mark: "chamber", name: "The Chamber of Nowhere", kind: "vault",
    difficulty: "Lord",
    flavor: "An arch on open sand with a green flame in it and no building attached, and the sand does not drift across the threshold.",
    foe: {
      name: "The Cartographer of Nowhere", ilk: "Phantom", deck: "starter_spectral_tiger", species: "Spectral Tiger", npcSkill: 0.72,
      lore: "The fifth Steward, and the only one with a hobby. Draws the desert. Has drawn it four hundred times and no two maps agree, and she has worked out that they are all correct and has been quietly delighted about it for years.",
    },
    hint: "Four hundred maps and no two agree, and she says they are all right. She will open for something that is also in two places at once and honest about it. Bring what the tide passes through, and a Natural Lord.",
    secretReq: { all: [{ cls: "Phantom" }, { natural3: true }] },
    secretFlavor: "She adds you to the map. All four hundred of them, at once, in different places.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AES-34", region: "desert", mark: "shard", name: "The Second Sun", kind: "fort",
    difficulty: "Lord", shards: 2,
    flavor: "A crater of glass at the north edge with something still lit at the bottom of it. It has been going out for a very long time and is not there yet.",
    foe: {
      name: "Vitrine", title: "the Second Sun", ilk: "Time", deck: "starter_z4z", species: "Z4Z", npcSkill: 0.74,
      lore: "Whatever came down here is still down here. It has been cooling since before the Boneyard had bones in it and it has learned to talk in that time, which nobody asked it to do.",
    },
    hint: "It has been going out since before the yard had bones in it and it is not finished. It will only look up for something that has also refused to stop. Bring a being that has come back from the Necropolis once already.",
    secretReq: { cls: "Phantom" },
    secretFlavor: "It looks up. The crater is a great deal deeper than it was a moment ago.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },

  // ══════════════════════════════════════════════════════════════
  // ZONE F · BONEYARD NORTH — Dragon and Time
  // ══════════════════════════════════════════════════════════════
  // The south yard is where things were PUT. The north yard is where
  // things came to be put, under their own power, having decided.
  {
    id: "AFC-35", region: "boneyard_north", mark: "cursed", name: "The Tallow Line", kind: "battle",
    difficulty: "Champion",
    flavor: "Candles set on skulls, in a line running north as far as the light goes, and every one of them is the same height.",
    foe: {
      name: "Tallow", ilk: "Time", deck: "starter_dg0", species: "DG-0", npcSkill: 0.62,
      lore: "Trims them so they burn level. Has done it so long that he trims by feel in the dark, and if you ask him what the line is FOR he will say it is for walking beside, which is true and is not an answer.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Every candle in the line is the same height, which is either devotion or arithmetic. Bring something that would know the difference.",
    secretReq: { all: [{ ilk: "Machine" }, { cls: "Time" }] },
    secretFlavor: "It measures them. Every one. Tallow watches the count with an expression nobody has ever seen on him.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AFC-36", region: "boneyard_north", mark: "cursed", name: "The Skull Ledger", kind: "battle",
    difficulty: "Champion",
    flavor: "A cliff of skulls with numbers painted on them in a neat hand, and the numbering starts at the top.",
    foe: {
      name: "Skull-Ledger Pinn", ilk: "Time", deck: "starter_antdroid", species: "Antdroid", npcSkill: 0.64,
      lore: "Numbers them. Started at the top because he expected the pile to get shorter, and has been adding negative numbers at the bottom for six years without ever admitting the mistake out loud.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "A cliff of skulls, numbered in a neat hand, and the numbering starts at the top. Bring something that belongs near the top.",
    secretReq: { natural3: true },
    secretFlavor: "Pinn writes a number nobody has needed before and looks quietly pleased about the paperwork.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AFC-37", region: "boneyard_north", mark: "cursed", name: "The Herald's Field", kind: "battle",
    difficulty: "Lord",
    flavor: "Open ground, swept, with a single standard planted in the middle of it and no army anywhere.",
    foe: {
      name: "The Carrion Herald", ilk: "Time", deck: "starter_z4z", species: "Z4Z", npcSkill: 0.7,
      lore: "Announces arrivals. There have been none. He announces them anyway, at dawn, to the field, and the accuracy of the announcements has become genuinely unsettling to the two or three people in a position to check.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Swept ground, one standard planted in the middle of it, and no army anywhere. It is not waiting for a fight. It is waiting for a claimant.",
    secretReq: { all: [{ cls: "Dragon" }, { natural3: true }] },
    secretFlavor: "The Herald lowers the standard. It does not hand it over. It simply stops holding it up.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AFC-38", region: "boneyard_north", mark: "cursed", name: "The Ashcroft Pyre", kind: "battle",
    difficulty: "Lord",
    flavor: "A pyre built and never lit, dressed and re-dressed until the wood has gone to stone under its own weight.",
    foe: {
      name: "Brand Ashcroft", ilk: "Time", deck: "starter_dg0", species: "DG-0", npcSkill: 0.72,
      lore: "Waiting for the one it is for. Has turned away eleven people who tried to use it, politely, and has kept the tinder dry for so long that lighting it now would be an act of demolition rather than of fire.",
    },
    // ⭐ AN EGG ON EVERY NODE (caP'n, 2026-08-22): *"every single node has
    // an easter egg that drops the mythic version of the fable version."*
    hint: "Built, dressed and re-dressed until the wood went to stone under its own weight. It has never been lit because nobody has ever been worth lighting it for.",
    secretReq: { all: [{ cls: "Time" }, { natural3: true }] },
    secretFlavor: "Brand Ashcroft strikes it. The stone burns, which it should not, and neither of you says so.",
    // ⚠ NO DIVINE CREST. Only the twenty-eight nodes that already
    // carried an egg pay the reserve; these twenty-seven pay in
    // Mythic cards alone. caP'n: *"maybe the other non-28 nodes
    // don't give you divine crests just cards but the 28 do."*
    reward: { crests: 1 },
  },
  {
    id: "AFS-39", region: "boneyard_north", mark: "shard", name: "The Long Winter", kind: "fort",
    difficulty: "Lord", shards: 2, trophy: "zhefir_trophy",
    flavor: "The north point, and the only cold place in the Minerium. There is frost on the bones here and there is no season here to have made it.",
    // ⭐ DRAGON 2 OF 3.
    foe: {
      name: "Bael", title: "the Long Winter", ilk: "Dragon", deck: "starter_premier_dragon", species: "Premier Dragon", npcSkill: 0.78,
      lore: "Came north to die a long time ago, got comfortable, and has been dying at his own pace ever since. The frost is his. He is not sorry about it and he is not finished, and he considers those the same statement.",
    },
    hint: "He came north to die and has been at it for an age, and the frost is his. He will not stir for anything that has not also outlasted its own ending. Bring a Dragon that has already been through every level it has.",
    secretReq: { all: [{ cls: "Dragon" }, { natural3: true }] },
    secretFlavor: "The frost goes off the bones in one breath. It comes back before you have finished watching it go.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },

  // ══════════════════════════════════════════════════════════════
  // ZONE G · SHV's CRYSTAL MAUSOLEUM — Space and Time
  // ══════════════════════════════════════════════════════════════
  // The second one. He built the first out of stone and did not like
  // what it said about him, so he built this one out of light and has
  // not liked that either. Three Sol Kings in one zone, which is the
  // densest concentration in the world and is the point of it.
  {
    id: "AGS-40", region: "crystal", mark: "shard", name: "The Seventh Facet", kind: "fort",
    difficulty: "Lord", shards: 2,
    flavor: "The eastern face of the crystal. Six facets take the light and pass it on. The seventh keeps it.",
    foe: {
      name: "Prism", title: "the Seventh Facet", ilk: "Space", deck: "starter_h01", species: "H-01", npcSkill: 0.76,
      lore: "An H-01 who came to hold the light for one century and has now held it for three. The facet has taken most of the way in. She is very carefully not letting go of either, and it is no longer obvious which of them is doing the holding.",
    },
    hint: "Six faces pass the light on and this one keeps it. It will not open its hand for anything that has not also kept something it should have passed on. Bring what walks between the stars.",
    secretReq: { cls: "Space" },
    secretFlavor: "It lets go. Two hundred years of light leaves in about a second and a half.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AGS-41", region: "crystal", mark: "shard", name: "The Cathedra", kind: "fort",
    difficulty: "Lord", shards: 2,
    flavor: "A seat cut into the crystal at the height of a Lord, facing out. It has never been sat in and the crystal underneath it is worn.",
    foe: {
      name: "Cathedra", ilk: "Time", deck: "starter_antdroid", species: "Antdroid", npcSkill: 0.78,
      lore: "The seat is the Sol King. An Antdroid of the Colonel's own line was set to keep the chair warm on the day it was cut, and has kept it warm for so long that the two of them have grown one shape between them. It has worked out, with the enormous patience available to furniture, that he never will — and it has decided to go on waiting anyway, which is the part that makes it dangerous.",
    },
    hint: "It has been waiting for a man who is not coming and it knows he is not coming. It will rise for something else that stayed anyway. Bring a Natural Lord.",
    secretReq: { natural3: true },
    secretFlavor: "It offers you the seat. Sitting in it would be the single worst decision available on this island.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AGS-42", region: "crystal", mark: "shard", name: "The Counted Light", kind: "fort",
    difficulty: "Lord", shards: 2,
    flavor: "A chamber inside the crystal where the light comes in one colour and leaves in eleven, and somebody has scratched a tally on the wall for each.",
    foe: {
      name: "Nel", title: "Who Counted the Light", ilk: "Space", deck: "starter_h09", species: "H-09", npcSkill: 0.8,
      lore: "Got to eleven. Has been trying to get to twelve for most of a lifetime, on the reasonable grounds that a thing which splits eleven ways has no business stopping at a prime.",
    },
    hint: "Eleven colours, and she wants a twelfth. Bring her something that is a colour this Minerium has not seen — a being out of the deep time.",
    secretReq: { cls: "Time" },
    secretFlavor: "Twelve. She sits down on the floor of her own chamber and does not get up for some while.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AGL-43", region: "crystal", mark: "chamber", name: "The Crystal Chamber", kind: "vault",
    difficulty: "Lord",
    flavor: "The base of the great crystal, where a green flame stands in an arch of light. It is the only thing in this building that casts a shadow.",
    foe: {
      name: "The Crystal Chamberlain", ilk: "Space", deck: "starter_h09", species: "H-09", npcSkill: 0.82,
      lore: "The sixth Steward and the last of them. Runs a building made of light for an Antdroid who has never entered it, and is the only one of the six who has understood what the flames are and has decided to say nothing to the other five.",
    },
    hint: "The only shadow in a building of light, and he knows exactly why. He will open for a Natural Lord that has walked between the stars — and for nothing that has not been all the way to the top.",
    secretReq: { all: [{ natural3: true }, { cls: "Space" }] },
    secretFlavor: "He tells you what the six flames are for. He is the only one who knows and he has been waiting a long time to be asked.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },

  // ══════════════════════════════════════════════════════════════
  // ZONE S · THE EYELAND — all four Ilks
  // ══════════════════════════════════════════════════════════════
  // ⚠ NO ROAD REACHES THIS ISLAND. Six Lord's Chambers, six ring
  // keepers, and a private beacon between each pair — that is the ONLY
  // way onto the Eyeland, and it is the reward for answering a
  // Chamber's riddle rather than merely beating it. See BEACONS.
  //
  // ⭐ AND THE PUPIL IS OPENED TWO WAYS. caP'n, 2026-08-21:
  //   *"You can access the middle of the eye by completing the 6 S
  //    nodes surrounding it… the other way is by completing the 4
  //    easter eggs on the Crimson Dais."*
  // See eyeOpen(). Doing it one way does NOT hand you the other way's
  // rewards — opening the pupil from the Dais leaves all six ring eggs
  // exactly where they were.
  {
    id: "ASS-48", region: "eyeland", mark: "shard", name: "The Upper Lid", kind: "fort",
    difficulty: "Lord", shards: 1,
    flavor: "The north arc of the ring. From here the pupil is looking somewhere over your shoulder and it is very difficult to stop checking.",
    foe: {
      name: "Palpebra", ilk: "Space", deck: "starter_h01", species: "H-01", npcSkill: 0.84,
      lore: "One of six who stand on the ring facing outward. She has never turned round. She says that whatever the eye is looking at is not her business and that the six of them are not guards, they are a courtesy.",
    },
    hint: "She has never once turned round, and says the six of them are a courtesy rather than a guard. Bring her something that also came the long way on purpose — what walks between the stars.",
    secretReq: { cls: "Space" },
    secretFlavor: "She turns round. It takes about four seconds and it is the loudest thing on this island.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "ASS-47", region: "eyeland", mark: "shard", name: "The White", kind: "fort",
    difficulty: "Lord", shards: 1,
    // ⭐ DRAGON 3 OF 3 was NOT put here — see the cap note at the top
    // of this table. The Eyeland runs Phantom, Space and Time and gets
    // its fourth Ilk from the Dais instead.
    flavor: "The north-east arc, and the brightest ground in the Minerium. Nothing casts a shadow standing on it, including you.",
    foe: {
      name: "Sclerid", ilk: "Time", deck: "starter_dg0", species: "DG-0", npcSkill: 0.84,
      lore: "Keeps the white white. Has never explained what would happen if it stopped being white and gives every impression of being the only person here who knows.",
    },
    hint: "Nothing standing here casts a shadow, and he intends to keep it that way. Bring something that has no shadow to give up — a being the light goes through.",
    secretReq: { cls: "Phantom" },
    secretFlavor: "For a moment there are two shadows on the white and only one of you.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "ASS-46", region: "eyeland", mark: "shard", name: "The Outer Ring", kind: "fort",
    difficulty: "Lord", shards: 1,
    flavor: "The south-east arc. The stone under your feet is banded like the rings in a felled tree and there are far too many bands.",
    foe: {
      name: "Corolla", ilk: "Time", deck: "starter_z4z", species: "Z4Z", npcSkill: 0.86,
      lore: "Counts the bands. Is up to a number she will not say out loud in company, and has taken to saying that the eye is not old so much as it has been awake a long time, which are different measurements.",
    },
    hint: "She counts the rings and will not say the number. Bring her a being out of the deep time and she may say it to that instead.",
    secretReq: { cls: "Time" },
    secretFlavor: "She says the number. It is not a large number and that is much worse.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "ASS-45", region: "eyeland", mark: "shard", name: "The Tear Duct", kind: "fort",
    difficulty: "Lord", shards: 1,
    flavor: "The south arc, where the island drains. Whatever comes out here goes off the edge of the world and there is no sound of it landing.",
    foe: {
      name: "Weep", ilk: "Space", deck: "starter_xulan", species: "Xulan", npcSkill: 0.86,
      lore: "Stands at the outflow. Catches nothing, stops nothing, redirects nothing — is simply present for it, which he insists is a duty and not a habit.",
    },
    hint: "He catches nothing and stops nothing and calls standing there a duty. Bring something that also goes where it is not caught, and a Natural Lord to bring it.",
    secretReq: { all: [{ cls: "Space" }, { natural3: true }] },
    secretFlavor: "He steps aside from the outflow for the first time. Nothing changes, and he looks devastated.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "ASS-44", region: "eyeland", mark: "shard", name: "The Lower Lid", kind: "fort",
    difficulty: "Lord", shards: 1,
    flavor: "The south-west arc. The ground is very slightly convex here, in the way a lid is, and standing on it for long is unpleasant for reasons the body works out before the mind does.",
    foe: {
      name: "Nictra", ilk: "Phantom", deck: "starter_inverted_serpent", species: "Inverted Serpent", npcSkill: 0.86,
      lore: "The only one of the six who blinks, and she does it for the whole island. Twice a year. Nobody who has been present for it has been willing to describe what the dark is like.",
    },
    hint: "She blinks for the whole island, twice a year, and no one who has seen it will describe the dark. Bring a being that lives in it.",
    secretReq: { cls: "Phantom" },
    secretFlavor: "She blinks. It is not twice a year today and she did not mean to.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "ASS-49", region: "eyeland", mark: "shard", name: "The Blind Spot", kind: "fort",
    difficulty: "Lord", shards: 1,
    flavor: "The north-west arc, and the one place on the ring the pupil cannot see. Everyone who arrives here relaxes and then works out why they relaxed.",
    foe: {
      name: "Scotoma", ilk: "Phantom", deck: "starter_shadow_fox", species: "Shadow Fox", npcSkill: 0.88,
      lore: "Guards the one patch of this island that is not observed, on the reasonable grounds that it is therefore the only patch worth guarding.",
    },
    hint: "The one patch of this island the eye cannot see, and he guards it for exactly that reason. Bring something else that arrives unobserved — and it had better have gone all the way to the top.",
    secretReq: { all: [{ cls: "Phantom" }, { natural3: true }] },
    secretFlavor: "He steps out of the blind spot. He is instantly and obviously smaller.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "ASS-50", region: "eyeland", mark: "pupil", name: "The Pupil", kind: "star",
    difficulty: "Lord", shards: 2, trophy: "jynxi",   // the mythic. The eye was always hers.
    flavor: "The middle of the eye. There is no ground here. There is a floor, which is a different thing, and it is warm.",
    foe: {
      name: "S'ight", title: "What It Is Looking At", ilk: "Time", deck: "starter_antdroid", species: "Antdroid", npcSkill: 0.9,
      lore: "The joke of the name is said out loud once and never again: the thing in the middle of the eye is not the eye's owner, it is the eye's SUBJECT. Something has been watching this for a very long time and the island grew around the looking.",
    },
    hint: "The eye is not looking out of this room, it is looking INTO it, and the thing it is looking at is looking back. Bring a Natural Lord and something out of the deep time, and do not blink first.",
    secretReq: { all: [{ natural3: true }, { cls: "Time" }] },
    secretFlavor: "The pupil moves off you and onto the thing you brought, and the relief is enormous and lasts about a second.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },

  // ══════════════════════════════════════════════════════════════
  // ZONE Z · THE CRIMSON DAIS — all four Ilks, and the end
  // ══════════════════════════════════════════════════════════════
  // ⚠ NO ROAD REACHES THIS EITHER, AND THAT IS DELIBERATE. caP'n:
  // *"onto Dais has no way in."* THE MOON is the only door and it opens
  // at fifteen Sol Shards, and it puts the player down on AZS-52.
  //
  // The four wardens ring the centre and the centre is the end of the
  // story. Beating them is not enough — their EGGS are what opens the
  // Jewel, and those same four eggs are the second key to the Pupil.
  {
    id: "AZS-52", region: "dais", mark: "shard", name: "The Hollow", kind: "ghost",
    difficulty: "Lord", shards: 1, daisArrival: true,
    flavor: "The west corner of the dais, and the first thing the Moon shows you. Something wore this shape once and left it standing.",
    foe: {
      name: "Nought", ilk: "Phantom", deck: "starter_spectral_tiger", species: "Spectral Tiger", npcSkill: 0.88,
      lore: "Not a ghost and not a guard — the space a Spectral Tiger warden used to occupy, holding the post out of shape memory. It fights exactly as well as whoever it used to be, which is the unsettling part, because whoever it used to be was very good.",
    },
    hint: "Something wore this and left it standing, and it has kept the shape out of habit. Bring the same trick — a being that is a shape with nothing solid in it.",
    secretReq: { cls: "Phantom" },
    secretFlavor: "It stops holding the shape. What is underneath is a great deal smaller and considerably older.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AZS-55", region: "dais", mark: "shard", name: "The Brood Gallery", kind: "battle",
    difficulty: "Lord", shards: 1,
    flavor: "The north corner. The walls are warm and they are not supposed to be, and the warmth is coming from inside them.",
    // ⭐ DRAGON 3 OF 3, and the last one in the Minerium. A clutch is
    // the right place for the rarest thing in the world to be kept.
    foe: {
      name: "Ythra", title: "the Last Clutch", ilk: "Dragon", deck: "starter_chaos_dragon", species: "Chaos Dragon", npcSkill: 0.9,
      lore: "Sitting on the last eggs anybody has seen in an age, on a slab of red stone hung in the dark with no road to it — which is either the safest place in the Unstable World or a decision made in a hurry. She has not moved off them to find out.",
    },
    hint: "She has not moved off them in an age and she is not going to for you. Bring the one thing she will stand up for: another of her own, grown all the way.",
    secretReq: { all: [{ cls: "Dragon" }, { natural3: true }] },
    secretFlavor: "She stands up. There are far fewer eggs under her than the size of her suggested.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AZS-54", region: "dais", mark: "shard", name: "The Pulp", kind: "battle",
    difficulty: "Lord", shards: 1,
    flavor: "The east corner. The floor gives when you stand on it and closes after you have gone, and it does not remember you afterwards.",
    foe: {
      name: "Sap", ilk: "Space", deck: "starter_h09", species: "H-09", npcSkill: 0.9,
      lore: "The dais is not stone all the way through and this is where it is not. Sap is the H-09 they sealed underneath, come up to the surface to see about the noise, and it has been coming up to see about the noise for several centuries and has never once been in a hurry.",
    },
    hint: "The floor closes behind you and forgets you, and so does he. Bring something that has been forgotten before and came back anyway — and bring it grown.",
    secretReq: { all: [{ cls: "Space" }, { natural3: true }] },
    secretFlavor: "The floor holds. It is the first time it has held for anyone and it is clearly an effort.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AZS-53", region: "dais", mark: "shard", name: "The Last Kiln", kind: "fort",
    difficulty: "Lord", shards: 1, trophy: "tyrent_trophy",   // the foe here IS T'yrent
    flavor: "The south corner. One fire, kept lit, in the one place in the world where nothing needs firing.",
    foe: {
      name: "T'yrent", ilk: "Time", deck: "starter_z4z", species: "Z4Z", npcSkill: 0.92,
      lore: "Kept one fire lit and has been feeding it with whatever came close for longer than the dais has been where it is. The name is the joke said out loud once — a tyrant is only something that would not let a thing go out.",
    },
    hint: "He has fed this fire with everything that came close for an age. Bring the one thing it cannot burn: something out of the deep time, standing at the top of itself.",
    secretReq: { all: [{ cls: "Time" }, { natural3: true }] },
    secretFlavor: "The fire goes out. He looks at the cold kiln for a long moment and then, visibly, relaxes.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
  {
    id: "AZS-56", region: "dais", mark: "pupil", name: "The Crimson Jewel", kind: "throne",
    difficulty: "Lord", boss: "hosh_vrown",
    species: "Antdroid",   // ⭐ THE SPECIES LAW — SHV is an Antdroid; see storyData.hosh_vrown
    flavor: "A held breath the size of a kingdom, and it has been held since before the Minerium changed hands.",
    // ⭐ FIGHT THREE, and caP'n's staging in his own order:
    //   1 · the Lyt Avatar — before you may even enter. A GATE.
    //   2 · the Lyf Avatar — he comes to YOU at eight Sol Shards,
    //       wherever you stand. An AMBUSH. See AMBUSHES.
    //   3 · HERE — the Moon at fifteen, and then the man.
    hint: "The jewel at the centre is not a door and there is no road to it. Answer all four keepers properly, and come with a Natural Lord standing. It will not wake for less.",
    // ⚠ THE HINT SAID "ANSWER ALL FOUR KEEPERS" AND THE REQUIREMENT
    // DID NOT ASK FOR IT. `{ emperium: "xaol" }` is satisfied the
    // instant `openEmperium` commits the fare — so the greatest-
    // rewards flag cost "have paid the Moon, and have a Natural Lord",
    // both of which the player necessarily holds by the time they are
    // standing here. The four wardens' puzzles were worth nothing at
    // the one node caP'n's ruling says they are worth everything at:
    // *"you defeat all 4 according to their puzzles and you can now
    // properly Solrend the Lord's Avatar — doing so according to its
    // puzzle yields the greatest rewards."*
    //
    // ⭐ AND IT LOCKS NOTHING, because this is the SECRET and not the
    // road. The four `via:"open"` edges onto the Jewel still ask only
    // that the wardens be CLEARED, so a player who cannot solve a
    // riddle can still finish the game — they simply do not get the
    // 1200 and the mythic set.
    secretReq: { all: [{ secret: ["AZS-52", "AZS-53", "AZS-54", "AZS-55"] }, { natural3: true }] },
    secretFlavor: "It does not rise. It simply stops pretending to be a stone.",
    reward: { crests: 1 }, prize: { crests: 1 },
  },
];

// Geometry, stapled on at module load. One pass, one place. A node
// that PIN forgot still exists and is still playable — it simply
// stands in the middle of the world until somebody drags it.
export const NODES = RAW_NODES.map((n) => {
  const p = PIN[n.id];
  // A node belongs to a Minerium. Omitted means the first one, which
  // is every node that exists today and is why this line changes
  // nothing. The parked-at-centre fallback asks THIS node's own
  // painting for its centre — Minerium 2's canvas will not be 841 by
  // 1264, and a forgotten pin should land in the middle of the map
  // the node is actually on rather than in the middle of Minerium 1.
  const world = n.world || FIRST_WORLD;
  const m = mapOf(world);
  if (!p) console.warn(`[worldMap] no PIN for "${n.id}" \u2014 parked at map centre`);
  return { ...n, world, mx: p ? p[0] : m.w / 2, my: p ? p[1] : m.h / 2 };
});

export const NODE_BY_ID = Object.fromEntries(NODES.map(n => [n.id, n]));
export const START_NODE = (NODES.find(n => n.start) || NODES[0]).id;

// ═══════════════════════════════════════════════════════════════
// PATHS
// ═══════════════════════════════════════════════════════════════
// Four orthogonal knobs, per the Exception Law:
//   via    — WHAT EVENT opens it:
//              "open"   already open, always
//              "clear"  opens when `from` is cleared        (default)
//              "secret" opens when `from`'s SECRET is found (implies hidden)
//              "warp"   opens when `from` is cleared, drawn as a warp
//   req    — WHAT ELSE must be true (any requirement object)
//   hidden — whether a CLOSED path renders at all
//   bend   — OPTIONAL waypoints, in MAP pixels, that the drawn road
//            and the walking avatar both bow through on the way:
//
//                bend: [[512, 880], [548, 940]]
//
//            This is the seam the painted world needed. Two pins can
//            sit on a road that CURVES between them, and a straight
//            line would cut across open void. Any path may carry any
//            number of waypoints; none is required, and a path with
//            no `bend` behaves exactly as it always did. It is one
//            data field, not a pathfinder — tracing a road is
//            content work, and `world_pinner.html` emits the array.
//
// Paths walk both ways once open, exactly like the SNES overworlds
// this is modelled on — except warps, which are one-way by nature.
// A reversed traversal walks its `bend` in reverse; the renderer
// handles that, so a waypoint list is authored ONE way only.
// `hint` is the line shown when a VISIBLE path is closed. Secrets
// never carry a hint; that's the difference between a locked door
// and a secret.

// ═══════════════════════════════════════════════════════════════
// ⚑ THE BEACON LAW  (August 17, 2026 — caP'n's hand-drawn guide)
// ═══════════════════════════════════════════════════════════════
// "You must land on the space on the board and complete an easter
//  egg there properly and then you can be transported to the
//  eyeball in the top left ACCORDING TO WHICH NODE YOU WERE ON.
//  They are not random, which is why I drew the lines."
//
// So the Eye is not one destination. It is TEN destinations, and
// which one you get is a property of where you left from. That is
// the whole mechanic, and this table is the whole of it — ONE row
// per drawn line, source on the left, landing on the right. To
// change where a beacon comes out, edit one row. Nothing else in
// this file, and nothing at all in WorldMap.js, has to move.
//
// ── THE TWO FAMILIES ────────────────────────────────────────
//   GREEN (caP'n's green lines) — the six painted SKULL ARCHES,
//   the Lord's Chambers. Six arches, six cracks in the Eye's ring.
//   One each, no sharing: a chamber owns its crack.
//
//   VIOLET (caP'n's purple lines) — the four shards of the Crimson
//   Dais, the small square island in the middle. "They all 4 go to
//   the middle eye node" — all four land on THE LASH, the pupil at
//   the centre of the ring. Four sources, one landing, by design.
//
// ── WHAT OPENS ONE ──────────────────────────────────────────
// `via: "warp"` means: opens when its source node is CLEARED.
// The easter eggs do not exist yet, so today "cleared" means
// winning the stop, and under THE SURVEYOR (dev) every beacon is
// charged from a cold save so the whole ring can be walked. When
// the easter eggs land they slot in as the clear condition for
// these ten sources and NOTHING here changes shape.
//
// ── A BEACON IS A PRIVATE DOOR, BOTH WAYS ───────────────────
// (caP'n, Aug 17: "each only connects to one red node and that red
//  node should only connect back to the castle.")
//
// So a beacon is not a one-way fling into the Eye and it is not a
// hub either. It is a PAIR. One chamber, one crack, and that
// crack's only yellow line in the whole world is the one going
// home. Walk the ring on foot and every crack you pass is a door
// back to the chamber that owns it — which makes the Eye a
// SWITCHBOARD you earn one plug at a time, rather than a room you
// get flung into and have to walk out of the long way.
//
// The four old exit warps off the ring — rent_1 to the Desert,
// rent_2 to the Gardens, and so on — are DELETED by this rule.
// They were exactly the "node connects to another node in yellow"
// shape caP'n ruled out, and the paired return makes them
// redundant besides.
//
// The dais four keep their own shape: four sources, one landing,
// and the pupil is the one place in the world that more than one
// yellow line touches. caP'n named that exception himself.
//
// ⚠ THE PAIRING IS caP'n's CALL. The six green pairings below are
// read off the drawn guide by bearing — chambers in clockwise
// order onto ring cracks in clockwise order. If a line lands on
// the wrong crack, swap the right-hand id on that row and nothing
// else. The violet four are certain; caP'n said all four.
// ═══════════════════════════════════════════════════════════════
// THE BEACONS — six chambers, six keepers, six private doors
// ═══════════════════════════════════════════════════════════════
// ⭐ THIS IS THE ONLY ROAD ONTO THE EYELAND, and it is why the six
// Lord's Chambers exist. There are exactly six Chambers scattered
// across six different zones and exactly six keepers standing on the
// Eye's ring, and the pairing is one-to-one.
//
// caP'n, Aug 17: *"each only connects to one red node and that red
// node should only connect back to the castle."* So a beacon is not a
// hub and not a one-way fling — it is a PAIR. One Chamber, one keeper,
// and that keeper's only yellow line in the whole world is the one
// going home. The Eye becomes a switchboard you earn one plug at a
// time.
//
// ── ⭐ via "secret", NOT "warp", AND THAT IS THE WHOLE DESIGN ────
// caP'n's ruling, PRIME §2.7: *"if you can solve the riddle/puzzle and
// fulfill the specific requirements and THEN annihilate the opponent
// of this node correctly based on the puzzle, you unlock the easter
// egg prize AND it also functions as a teleporter."* Prize and
// teleporter, both conditional on the puzzle. `pathState` gates a
// "secret" road on ctx.SECRETS, so a Chamber merely BEATEN opens
// nothing and a Chamber ANSWERED opens the island.
//
// ⚠ WHICH MEANS THE SIX CHAMBER RIDDLES ARE LOAD-BEARING. All six are
// satisfiable with an ordinary collection today — an Ilk, a Natural
// Lord, or both — and none of them depends on `secretReq.result`,
// which is still deferred. Do not make one of these harder without
// checking it is still answerable, because a Chamber nobody can solve
// is a keeper nobody can reach.
//
// The pairing is by BEARING: the Chamber in the north of the world
// lands on the north of the ring, and so on round. If a line should
// land somewhere else, swap the right-hand id and nothing else moves.
export const BEACONS = [
  { from: "AGL-43", to: "ASS-48" },   // Crystal Mausoleum (N)  → the Upper Lid
  { from: "AEL-33", to: "ASS-47" },   // Deserted Desert  (NE)  → the White
  { from: "ADL-24", to: "ASS-46" },   // Harsh Marsh East (SE)  → the Outer Ring
  { from: "ACL-26", to: "ASS-45" },   // SHV's Mausoleum  (S)   → the Tear Duct
  { from: "AAL-16", to: "ASS-44" },   // Boneyard South   (SW)  → the Lower Lid
  { from: "ABL-13", to: "ASS-49" },   // Harsh Marsh West (NW)  → the Blind Spot
];

// Every beacon is an ordinary one-way warp path. It is expanded
// here rather than hand-written into RAW_PATHS so the table above
// stays the only place a landing is ever stated.
const BEACON_PATHS = BEACONS.map(b => ({
  // oneWay:false — see A BEACON IS A PRIVATE DOOR above. The
  // reverse leg opens on the same condition as the forward one
  // (pathState reads `from`, which is always the chamber), so
  // clearing the chamber lights both directions at once and an
  // uncleared chamber is not reachable from inside the Eye either.
  // ⭐ via "secret", NOT "warp" — a beacon is the RIDDLE'S reward.
  //
  // caP'n's ruling, quoted verbatim in PRIME §2.7: "if you can solve
  // the riddle/puzzle and fulfill the specific requirements and THEN
  // annihilate the opponent of this node correctly based on the
  // puzzle, you unlock the easter egg prize AND it also functions as a
  // teleporter." Prize and teleporter, both conditional on the puzzle.
  // The banner twenty lines up in this very file repeats that reading.
  //
  // But these were built `via: "warp"`, and pathState gates a warp on
  // ctx.CLEARED — so every beacon opened on an ordinary win. The
  // consequence was worst at the start: veil_camp is the START NODE
  // and its beacon lands on rent_4, so a brand-new player's very first
  // victory handed them a one-tap teleport into the Lord-tier Rent.
  // The riddle was decorative and the difficulty curve had a hole in
  // it on turn one.
  //
  // `via: "secret"` makes pathState read ctx.SECRETS instead, which is
  // stamped by recordNodeClear when the fight was won with the secret
  // offered. Verified before changing: all ten beacon sources carry a
  // real, satisfiable secretReq today (an Ilk, a natural Lord, shards,
  // the children found) — the deferred part of the riddle system is
  // secretReq.RESULT, "how you won", not the requirements. Nothing is
  // orphaned either: every chamber is still reachable on foot, so this
  // removes a shortcut rather than a destination.
  from: b.from, to: b.to, via: "secret", oneWay: false, beacon: true,
}));

// ═══════════════════════════════════════════════════════════════
// THE ROAD — traced by caP'n's own hand, 2026-08-21
// ═══════════════════════════════════════════════════════════════
// caP'n: *"the yellow line is the player's path they walk on… the
// original path you made was very close but was off a bit, so I
// realized I should've just traced it myself and showed you so you can
// just copy it."*
//
// So this table is a COPY, not a derivation. The previous 91 rows were
// machine-traced off the painting and the tracer's guesses about which
// pad joined which are gone. Where a row here disagrees with the yellow
// ink, the ink is right and the row is a typo.
//
// ── THE FOUR KNOBS, unchanged ───────────────────────────────────
//   via    — "clear" (default) · "open" (always) · "warp" · "secret"
//   req    — the recursive requirement object. See meetsReq.
//   oneWay — a direction on purpose. Rare.
//   hidden — invisible until its condition is met.
//
// ⚠ ROADS ARE UNDIRECTED. `pathState` opens a `via:"clear"` road from
// EITHER end, so the from/to ordering below carries no meaning at all
// and never should. It reads west-to-east and south-to-north because
// that is the order the player meets them in.
const RAW_PATHS = [
  ...BEACON_PATHS,


  // ── A · BONEYARD SOUTH ──────────────────────────────────────
  // The stem up out of the Roadhead, then a loop. The loop is the
  // point: a new player gets a choice of four fights at roughly the
  // same difficulty and can lose one without being stuck.
  { from: "AAC-01", to: "AAC-02" },
  { from: "AAC-02", to: "AAC-03" },
  { from: "AAC-03", to: "AAS-04" },
  { from: "AAS-04", to: "AAC-05" },
  { from: "AAC-05", to: "AAC-06" },
  { from: "AAC-05", to: "AAC-07" },
  { from: "AAC-07", to: "AAC-09" },
  { from: "AAC-09", to: "AAC-08" },
  { from: "AAC-08", to: "AAC-06" },
  // The east spur — the Long Gate, the Bone Chamber, and the road out.
  { from: "AAS-04", to: "AAC-15" },
  { from: "AAC-15", to: "AAL-16" },

  // ── A → B · up into the marsh ───────────────────────────────
  { from: "AAC-09", to: "ABC-10" },
  // ── A → C · east, to the Mausoleum ──────────────────────────
  { from: "AAC-15", to: "ACC-17" },

  // ── B · HARSH MARSH WEST ────────────────────────────────────
  // A cul-de-sac, deliberately. Nothing leads out of the top of it.
  { from: "ABC-10", to: "ABC-11" },
  { from: "ABC-11", to: "ABC-12" },
  { from: "ABC-12", to: "ABL-13" },
  { from: "ABC-12", to: "ABS-14" },
  { from: "ABL-13", to: "ABS-14" },

  // ── C · SHV's MAUSOLEUM ─────────────────────────────────────
  // A ring around the spires with the Black Chamber in the middle of
  // it, reachable from the two long sides. The two thick arrows on
  // caP'n's trace are these.
  { from: "ACC-17", to: "ACC-18" },
  { from: "ACC-18", to: "ACC-20" },
  { from: "ACC-20", to: "ACS-19" },
  { from: "ACS-19", to: "ACC-21" },
  { from: "ACC-21", to: "ACC-23" },
  { from: "ACC-23", to: "ACC-22" },
  { from: "ACC-22", to: "ACC-25" },
  { from: "ACC-25", to: "ACC-27" },
  { from: "ACC-17", to: "ACL-26" },
  { from: "ACC-25", to: "ACL-26" },

  // ── C → D · the spit across the east water ──────────────────
  { from: "ACC-22", to: "ADL-24" },
  // ── C → E · the north stair, up into the sand ───────────────
  { from: "ACC-27", to: "AEC-28" },

  // ── E · DESERTED DESERT ─────────────────────────────────────
  // Splits at the Glassworks and comes back together under the
  // Second Sun, so the Hour-Thief and the Cartographer can be taken
  // in either order.
  { from: "AEC-28", to: "AEC-29" },
  { from: "AEC-29", to: "AEC-30" },
  { from: "AEC-29", to: "AEC-31" },
  { from: "AEC-30", to: "AES-32" },
  { from: "AEC-31", to: "AEL-33" },
  { from: "AES-32", to: "AES-34" },
  { from: "AEL-33", to: "AES-34" },

  // ── E → F · north, into the cold ────────────────────────────
  { from: "AES-34", to: "AFC-35" },

  // ── F · BONEYARD NORTH ──────────────────────────────────────
  // One line. No branches. The last stretch of walking in the world.
  { from: "AFC-35", to: "AFC-36" },
  { from: "AFC-36", to: "AFC-37" },
  { from: "AFC-37", to: "AFC-38" },
  { from: "AFC-38", to: "AFS-39" },

  // ── F → G · west, along the top of the world ────────────────
  { from: "AFS-39", to: "AGS-40" },

  // ── G · SHV's CRYSTAL MAUSOLEUM ─────────────────────────────
  // Three Sol Kings and a Chamber on one island — the densest zone
  // in the Minerium, and the end of the walkable road.
  { from: "AGS-40", to: "AGL-43" },
  { from: "AGL-43", to: "AGS-42" },
  { from: "AGL-43", to: "AGS-41" },
  { from: "AGS-42", to: "AGS-41" },

  // ── S · THE EYELAND — the ring ──────────────────────────────
  // ⚠ NOTHING ON THIS ISLAND CONNECTS TO THE MAINLAND. The only ways
  // on are a Lord's Chamber's beacon (see BEACONS) and THE EYE, once
  // it opens. The ring itself is an ordinary walk.
  { from: "ASS-49", to: "ASS-48" },
  { from: "ASS-48", to: "ASS-47" },
  { from: "ASS-47", to: "ASS-46" },
  { from: "ASS-46", to: "ASS-45" },
  { from: "ASS-45", to: "ASS-44" },
  { from: "ASS-44", to: "ASS-49" },

  // ── S · INTO THE PUPIL ──────────────────────────────────────
  // Six roads because the player may be standing anywhere on the ring
  // when it opens, and `req: { eye: true }` because THE EYE OPENS TWO
  // WAYS — six ring clears, or four Crimson Dais eggs. See eyeOpen().
  { from: "ASS-48", to: "ASS-50", via: "open", req: { eye: true } },
  { from: "ASS-47", to: "ASS-50", via: "open", req: { eye: true } },
  { from: "ASS-46", to: "ASS-50", via: "open", req: { eye: true } },
  { from: "ASS-45", to: "ASS-50", via: "open", req: { eye: true } },
  { from: "ASS-44", to: "ASS-50", via: "open", req: { eye: true } },
  { from: "ASS-49", to: "ASS-50", via: "open", req: { eye: true } },

  // ── Z · THE CRIMSON DAIS — the square ───────────────────────
  // ⚠ AND NOTHING CONNECTS TO THIS EITHER. THE MOON is the only door
  // and it lands on AZS-52. caP'n: *"onto Dais has no way in."*
  { from: "AZS-52", to: "AZS-55" },
  { from: "AZS-55", to: "AZS-54" },
  { from: "AZS-54", to: "AZS-53" },
  { from: "AZS-53", to: "AZS-52" },

  // ── Z · ONTO THE JEWEL ──────────────────────────────────────
  // ⚠ CLEARED, NOT PUZZLED, AND THAT IS A DELIBERATE SOFTENING.
  // caP'n's 2026-08-18 ruling reads *"you defeat all 4 according to
  // their puzzles and you can now properly Solrend the Lord's Avatar…
  // doing so according to its puzzle yields the greatest rewards."*
  // Gating the ROAD on four eggs would put the ending of the game
  // behind four of the hardest riddles in it, with no way to finish
  // for a player who cannot solve one. So beating the four opens the
  // door, and answering the four is what `emperiumState.avatarOpen`
  // reports and what the Jewel's own secretReq pays for — the
  // "greatest rewards" half, intact, without the lockout.
  { from: "AZS-52", to: "AZS-56", via: "open", req: { cleared: ["AZS-52", "AZS-53", "AZS-54", "AZS-55"] } },
  { from: "AZS-55", to: "AZS-56", via: "open", req: { cleared: ["AZS-52", "AZS-53", "AZS-54", "AZS-55"] } },
  { from: "AZS-54", to: "AZS-56", via: "open", req: { cleared: ["AZS-52", "AZS-53", "AZS-54", "AZS-55"] } },
  { from: "AZS-53", to: "AZS-56", via: "open", req: { cleared: ["AZS-52", "AZS-53", "AZS-54", "AZS-55"] } },
];

// A stable id for one edge. Lives here rather than in the renderer
// because PATHS live here, and the screen tags DOM nodes with it.
export const edgeKey = (p) => `${p.from}>${p.to}`;

// Waypoints, stapled on the same way the pins were. RAW_PATHS stays
// pure graph — who connects to whom and what opens it — and never
// carries a pixel.
export const PATHS = RAW_PATHS.map((p) => {
  const b = BEND[edgeKey(p)];
  // A road's world is its origin's world; nothing needs to say so on
  // the row. A road BETWEEN two Mineriums is not a road at all — that
  // is WORLD_LINKS, and it is a door, not a walk.
  const world = NODE_BY_ID[p.from]?.world || FIRST_WORLD;
  return b ? { ...p, world, bend: b } : { ...p, world };
});

// ── WHAT ONE MINERIUM OWNS ──────────────────────────────────────
// The screen filters; the save file does not. WorldMap renders and
// walks ONE painting at a time and these are how it asks for it. Both
// are O(n) over ~50 rows and are called from a useMemo — do not
// "optimise" them into a prebuilt index that then has to be kept in
// step with the tables above.
export const nodesIn = (worldId) => NODES.filter(n => n.world === worldId);
export const pathsIn = (worldId) => PATHS.filter(p => p.world === worldId);

// Which painting a node stands on. The map screen opens on whichever
// world the player's saved position belongs to, so a save that ends
// in Minerium 2 reopens there and never flashes Minerium 1 first.
export const worldOfNode = (nodeId) => NODE_BY_ID[nodeId]?.world || FIRST_WORLD;

// The full point list a road travels, start to end, in MAP pixels.
// This is the ONE place `bend` is unrolled, so the drawn route and
// the walking avatar can never disagree about where the road went.
export function routePoints(path, forward = true) {
  const a = NODE_BY_ID[path.from], b = NODE_BY_ID[path.to];
  if (!a || !b) return [];
  const pts = [
    { x: a.mx, y: a.my },
    ...(path.bend || []).map(([x, y]) => ({ x, y })),
    { x: b.mx, y: b.my },
  ];
  return forward ? pts : pts.slice().reverse();
}

// ═══════════════════════════════════════════════════════════════
// THE CAST — resolved, never re-typed
// ═══════════════════════════════════════════════════════════════
// Every named boss on this map already exists somewhere in the
// codebase. We look them up rather than copy them, so a dialogue
// edit in EndlessDepths or storyData lands here for free.

export function resolveBoss(bossId) {
  if (!bossId) return null;
  const gk = GATEKEEPERS.find(g => g.id === bossId);
  if (gk) return { ...gk, source: "gatekeeper" };
  const iv = (INNER_VILLAINS || []).find(v => v.id === bossId);
  if (iv) return { ...iv, source: "inner" };
  const op = OPPONENTS?.[bossId];
  if (op) return { ...op, source: "story" };
  return null;
}

// The opponent a node actually fields. Authored → the named cast
// member. Unauthored → a procedural Depths brain scaled to the
// region. Either way the node is playable. THE FLOOR LAW.
export function opponentForNode(node) {
  const region = REGION_BY_ID[node.region];
  const boss = resolveBoss(node.boss);
  // ── THE MINERIUM OVERLAY ────────────────────────────────────
  // The cast is shared with the Endless Depths, where V'ael says
  // "the descent ends here" — a line about a LADDER, said in a
  // marsh. So a node may override what its opponent says HERE,
  // without forking the character. Stats, deck and brain stay in
  // one place; the words are per-Minerium. See minerium1.js.
  const scene = sceneFor(node.id);
  if (boss) {
    return {
      id: boss.id,
      name: boss.name,
      title: boss.title || null,
      difficulty: node.difficulty || boss.difficulty || "Champion",
      starterDeckId: boss.starterDeckId || null,
      npcSkill: boss.npcSkill ?? null,
      temperament: boss.temperament || null,
      accentColor: boss.accentColor || region?.color || "#c8963c",
      avatarIcon: boss.avatarIcon || "\u25c6",
      // Merged, not replaced: a scene that authors only `approach`
      // leaves the character's own onWin/onLoss standing.
      dialogue: (scene || boss.dialogue) ? { ...(boss.dialogue || {}), ...(scene || {}) } : null,
      // THE SECOND WIND — a boss with `phases` fights more than once
      // for one node. Passed through untouched; the engine reads it.
      phases: boss.phases || null,
      species: boss.species || node.species || null,   // ⭐ THE SPECIES LAW — see the foe branch
      isBoss: true,
    };
  }
  // ═══════════════════════════════════════════════════════════
  // ⭐ THE NODE'S OWN PERSON — new 2026-08-21
  // ═══════════════════════════════════════════════════════════
  // caP'n: *"you can create names and stories etc for all the node
  // opponents and Sol King, Lord's Chambers, all of it."* So every
  // node in this Minerium carries a `foe`, and this is where it is
  // read. `generateOpponent` below is now the FALLBACK — for a node
  // nobody has written yet, which in Minerium 1 is none of them.
  //
  // ⚠ THE DECK IS THE ZONE'S ILK LAW MADE REAL. `foe.deck` is a real
  // STARTER_DECKS id and it is what the duel actually deals the
  // adversary — see App.handleEnterNPC, which until today ignored the
  // field entirely and rolled a random deck for every opponent in
  // every mode. Without that fix the whole Ilk law would have been
  // decoration.
  if (node.foe) {
    const f = node.foe;
    return {
      id: `foe_${node.id}`,
      name: f.name,
      title: f.title || null,
      difficulty: node.difficulty || "Beb",
      starterDeckId: f.deck || null,
      npcSkill: f.npcSkill ?? null,
      temperament: f.temperament || null,
      accentColor: f.accentColor || region?.color || "#c8963c",
      avatarIcon: f.avatarIcon || MARK_ROLES[node.mark]?.glyph || "◆",
      ilk: f.ilk || null,
      // ⭐ THE SPECIES LAW (caP'n, 2026-08-22): *"every single being in
      // the minerium has to be a species of the canon current Terror,
      // do not create random species etc, they must be from the 12
      // species I have provided already."* Every foe now carries one,
      // derived from its deck and never invented, and it is passed
      // through here so the panel can SAY it — data nobody can read is
      // data nobody maintains.
      species: f.species || node.species || null,
      lore: f.lore || null,
      // The Minerium overlay still wins where it exists, so a scene
      // authored in minerium1.js overrides a foe's own words.
      dialogue: scene ? { ...scene } : null,
      isBoss: false,
    };
  }

  const proc = generateOpponent(region?.brainElo || 400, node.difficulty || "Beb");
  return {
    ...proc,
    isBoss: false,
    accentColor: region?.color || proc.accentColor,
    // A procedural opponent has no words of its own, but the NODE may.
    // Without this a scene authored for an unbossed node would be
    // silently dropped — and silently is the bad half of that sentence.
    dialogue: scene ? { ...scene } : (proc.dialogue || null),
  };
}

// ═══════════════════════════════════════════════════════════════
// THE REQUIREMENT RESOLVER
// ═══════════════════════════════════════════════════════════════
// One recursive function over a data object. Every key ANDs with
// every other key; array values mean "any of". Combinators: all /
// any / not. Returns { ok, why } so a VISIBLE locked path can
// explain itself and a hidden one can stay quiet.
//
// Adding a new primitive is a case here plus a field in buildCtx —
// it is deliberately NOT a closed set.

const asList = (v) => (v == null ? [] : Array.isArray(v) ? v : [v]);

export function meetsReq(req, ctx) {
  if (SURVEYOR) return { ok: true };   // ⚠ test dial — see MAP block
  if (!req) return { ok: true };
  const fail = (why) => ({ ok: false, why });

  if (req.all) {
    for (const r of req.all) { const s = meetsReq(r, ctx); if (!s.ok) return s; }
  }
  if (req.any) {
    const subs = req.any.map(r => meetsReq(r, ctx));
    if (!subs.some(s => s.ok)) return fail(subs.find(s => s.why)?.why || "Another way in is needed.");
  }
  if (req.not) {
    if (meetsReq(req.not, ctx).ok) return fail("Something here is already spoken for.");
  }

  for (const id of asList(req.cleared)) {
    if (!ctx.cleared.has(id)) return fail(`${NODE_BY_ID[id]?.name || "Somewhere else"} first.`);
  }
  for (const id of asList(req.secret)) {
    if (!ctx.secrets.has(id)) return fail("There is a way you have not found yet.");
  }
  for (const id of asList(req.found)) {
    if (!ctx.found.has(id)) return fail(`You have not found ${childName(id)}.`);
  }

  // ── THE TWO DESCRIPTOR GATES ────────────────────────────────
  // `req.ilk` names an ILK — Humanoid / Beast / Machine / Dragon.
  // `req.cls` names a SCHOOL — Space / Phantom / Dragon / Time.
  // Both keys and both sentences were the other way round until
  // 2026-08-25, when cards.js stopped storing the two words under
  // inverted names. The node data was re-keyed in the same pass and
  // the two sentences travelled with their own word family, so every
  // door in the Minerium opens for exactly the being it always did and
  // says exactly what it always said.
  const ilks = asList(req.ilk);
  if (ilks.length && !ilks.some(i => ctx.ilks.has(i))) {
    return fail(`Bring a ${ilks.join(" or ")}.`);
  }
  const schools = asList(req.cls);
  if (schools.length && !schools.some(c => ctx.schools.has(c))) {
    return fail(`Bring a ${schools.join(" or ")} being.`);
  }
  for (const id of asList(req.owns)) {
    if (!ctx.owned.has(id)) return fail("Something in your collection is missing.");
  }
  for (const id of asList(req.inDeck)) {
    if (!ctx.cards.has(id)) return fail("Something in your Grimoire is missing.");
  }
  const comps = asList(req.companion);
  if (comps.length && !comps.includes(ctx.companion)) {
    return fail("A different companion would be welcome here.");
  }
  if (req.natural3 && !ctx.natural3) return fail("Bring a Natural Level 3.");
  // The two Story Mode primitives. Both are ordinary keys in the
  // recursive object, so "a route that opens once the Minerium is
  // paid for AND you still hold three shards" is a data row.
  if (req.shards != null && ctx.shards < req.shards) {
    return fail(`${req.shards} Sol Shards.`);
  }
  // ⭐ HELD vs EARNED (2026-08-20). `shards` is what you are CARRYING
  // and is the right primitive for a FARE — it goes down when you pay
  // one. `shardsEarned` is how many keepers you have beaten and never
  // goes down, which is the right primitive for a RIDDLE about what you
  // have collected. Mixing them up locked The Shard Chamber's egg
  // permanently behind the Moon's 15-shard purchase.
  if (req.shardsEarned != null && (ctx.shardsEarned || 0) < req.shardsEarned) {
    return fail(`${req.shardsEarned} Sol Shards, ever.`);
  }
  // ⭐ `eye` — the pupil's two keys, in one primitive. See eyeOpen().
  // Declared as a req rather than hardcoded into the six roads so the
  // rule lives in one function and the roads only ask.
  if (req.eye && !eyeOpen(ctx)) {
    return fail("The Eye has not opened to you.");
  }
  for (const id of asList(req.emperium)) {
    if (!ctx.emperiums.has(id)) return fail(`${EMPERIUM_BY_ID[id]?.name || "An Emperium"} is still sealed.`);
  }
  if (req.elo    != null && ctx.elo    < req.elo)    return fail(`Depth ${req.elo} first.`);
  if (req.crests != null && ctx.crests < req.crests) return fail(`${req.crests} Divine Crests.`);
  if (req.wins   != null && ctx.wins   < req.wins)   return fail(`${req.wins} victories.`);

  return { ok: true };
}

// Build the resolver context from the profile + the deck being
// carried. Deck shape is Terror's standard { grimoire, luxmoire },
// both maps of cardId → count.
export function buildCtx({ progress, deck, profile, companionId }) {
  const cardIds = new Set([
    ...Object.keys(deck?.grimoire || {}),
    ...Object.keys(deck?.luxmoire || {}),
  ]);

  const ilks = new Set();
  const schools = new Set();
  let natural3 = false;
  try {
    const all = getAllGrimoireCards();
    for (const c of all) {
      if (!cardIds.has(c.id)) continue;
      // c.ilk = Humanoid/Beast/Machine/Dragon; c.school = Space/Phantom/
      // Dragon/Time (cards.js, corrected 2026-08-25). Tomes and Curios
      // drop their school string into `ilks` as they always have —
      // harmless, since no gate asks for one.
      if (c.ilk) ilks.add(c.ilk);
      if (c.school) schools.add(c.school);
      if ((c.level ?? 0) >= 3) natural3 = true;
    }
  } catch (_) { /* non-fatal — an empty deck simply gates nothing open */ }

  // ── SHARDS HELD = EARNED MINUS SPENT ────────────────────────
  // Derived on every ctx build rather than stored as a number, for
  // the reason stated at SOL SHARDS above: a stored count is a
  // balance, and a balance is a thing a client can write. This is
  // arithmetic over two append-only stamp maps and cannot be
  // anything else. `spent` is keyed by the same node ids, so a
  // shard that paid for a door is gone and stays gone.
  const earnedShards = Object.keys(progress?.shards || {})
    .reduce((n, nodeId) => n + shardsFor(NODE_BY_ID[nodeId]), 0);
  const spentShards = Object.values(progress?.emperiums || {})
    .reduce((n, rec) => n + (Number(rec?.paid) || 0), 0);

  return {
    cleared:   new Set(Object.keys(progress?.cleared || {})),
    secrets:   new Set(Object.keys(progress?.secrets || {})),
    found:     new Set(Object.keys(progress?.found   || {})),
    shardNodes: new Set(Object.keys(progress?.shards || {})),
    shards:    Math.max(0, earnedShards - spentShards),
    // Monotonic. See HELD vs EARNED in meetsReq.
    shardsEarned: earnedShards,
    emperiums: new Set(Object.keys(progress?.emperiums || {})),
    ilks, schools, natural3,
    cards:     cardIds,
    owned:     new Set(Object.keys(profile?.collection || {})),
    companion: companionId || null,
    elo:       profile?.endlessDepths?.elo || 0,
    crests:    profile?.chocolateCoins || 0,
    wins:      profile?.totalWins || 0,
    // ⬡ V'AEL'S TOOLS — the ids you hold, and how far you have crept.
    // A Set like every other membership on this object, so a future
    // `req: { tool: "vaels_veil" }` is one line in meetsReq and no new
    // plumbing. Nothing gates on one today, deliberately.
    // ⛔ `veil` IS A STEP COUNT, NOT A BILL. The Veil is free — caP'n,
    // 2026-09-15: *"completely free :3 purely narrative"* — so the
    // `owed` map that used to ride along here is gone. See vaelsTools.js.
    tools:     new Set(Object.keys(progress?.[TOOL_KEEP] || {})),
    veil:      progress?.[LEDGER_KEEP] || { steps: 0 },
  };
}

// ═══════════════════════════════════════════════════════════════
// GRAPH DERIVATION
// ═══════════════════════════════════════════════════════════════

// Is this path currently walkable?
export function pathState(path, ctx) {
  const via = path.via || "clear";

  // ⚠ THE SURVEYOR. Returns the same SHAPE as a real answer — the
  // `warp` and `secret` flags are preserved, not flattened, because
  // the renderer colours a road off them and a test mode that made
  // every road look identical would hide the exact thing being
  // checked. Only the three gates are lifted.
  if (SURVEYOR) {
    return {
      open: true, eventOk: true, reqOk: true, why: null, visible: true,
      warp: via === "warp", secret: via === "secret",
    };
  }

  // ═══════════════════════════════════════════════════════════
  // ⭐ A ROAD IS OPEN FROM EITHER END. THIS WAS THE BIGGEST BUG
  //    IN TERROR AND IT WAS INVISIBLE IN DEVELOPMENT.
  // ═══════════════════════════════════════════════════════════
  // This line read `eventOk = ctx.cleared.has(path.from)` for BOTH
  // "clear" and "warp". For a beacon that is exactly right and it
  // stays (see below). For an ordinary road it was catastrophic.
  //
  // The 91 road rows were MACHINE-TRACED off the painting in August,
  // so which end of a road landed in `from` and which in `to` is an
  // artifact of the tracer's walk — it carries no meaning at all. But
  // the gate read `from` and only `from`, which turned every one of
  // those arbitrary orderings into a direction of travel. Nine nodes
  // never appear as a `to` anywhere in the table, so their only roads
  // demanded they be CLEARED BEFORE THEY COULD BE ENTERED.
  //
  // Measured over the real NODES/PATHS with the real gate logic:
  //
  //     from veil_camp, a player could reach 21 of 55 nodes.
  //
  // Unreachable were: throne_seal — the ONLY kind:"throne" node, so
  // storyComplete() could never be true and neither Story Set could
  // ever pour · every Lord's Chamber · veil_gate, which is the FIRST
  // scene in minerium1.js · both children's traces · the J'ynxi coin
  // beat · and m1_champion, which is the ending. Seven of the eight
  // authored scenes and all four interludes sat on nodes nobody could
  // stand on. Only 8 of 30 Sol Shards were earnable, so the 15-shard
  // Minerium door could not be paid either.
  //
  // ⚠ WHY NOBODY SAW IT: THE SURVEYOR short-circuits this whole
  // function twenty lines up, and the Surveyor has been ON since
  // 2026-08-18 by caP'n's own request. Every walk of the map since
  // then has been a walk with every gate lifted. The one mode that
  // exists to make development possible is the one that hid this.
  //
  // THE FIX IS SYMMETRY, AND ONLY FOR ORDINARY ROADS:
  //   · via "clear", not oneWay → EITHER end being cleared opens it.
  //     That is what an undirected road has always meant everywhere
  //     else in the file; the gate simply never agreed.
  //   · via "clear", oneWay     → `from` only. A one-way is a
  //     direction on purpose.
  //   · via "warp" (BEACONS)    → `from` only, ALWAYS. The beacon
  //     comment says it outright: "pathState reads `from`, which is
  //     always the chamber, so clearing the chamber lights both
  //     directions at once and an uncleared chamber is not reachable
  //     from inside the Eye either." Making beacons symmetric would
  //     hand the player every Lord's Chamber the moment they set foot
  //     in the Rent, which is the opposite of earning a switchboard
  //     one plug at a time.
  //   · via "secret"            → `from` only. A secret is found AT a
  //     place; that is not a road being walkable, it is a thing being
  //     known.
  //
  // After the fix the same walk reaches 50 of 55. The last five —
  // the four Crimson Dais gems and the Jewel — are a separate,
  // AUTHORING gap and not a code one: the Dais has no inbound edge of
  // any kind, only its own closed four-gem ring, its beacons to the
  // pupil, and the shard-priced teleport onto the Jewel. See
  // auditWorldReachability() at the bottom of this file, which now
  // reports exactly this and fails loudly the next time a traced edge
  // orphans a region.
  let eventOk = true;
  if (via === "warp")        eventOk = ctx.cleared.has(path.from);
  else if (via === "secret") eventOk = ctx.secrets.has(path.from);
  else if (via === "clear")  eventOk = ctx.cleared.has(path.from)
                                    || (!path.oneWay && ctx.cleared.has(path.to));
  // via "open" is always eventOk

  const reqState = meetsReq(path.req, ctx);
  const hidden = path.hidden ?? (via === "secret");

  return {
    open: eventOk && reqState.ok,
    eventOk,
    reqOk: reqState.ok,
    why: reqState.why || null,
    // A secret path is invisible until its secret is found; after
    // that it draws like any other route. A `hidden` req-gated path
    // stays invisible until its requirement is met.
    visible: hidden ? eventOk && (reqState.ok || via === "secret") : true,
    warp: via === "warp",
    secret: via === "secret",
  };
}

// Every node you can currently stand on: a walk of the open graph
// from the start node. Cleared nodes are always included, so a
// player can never be stranded by an authoring change.
export function reachableSet(ctx) {
  const open = PATHS.map(p => ({ p, s: pathState(p, ctx) })).filter(x => x.s.open);
  const adj = new Map();
  const push = (a, b) => { if (!adj.has(a)) adj.set(a, new Set()); adj.get(a).add(b); };
  for (const { p } of open) {
    push(p.from, p.to);
    if (!p.oneWay) push(p.to, p.from);
  }
  const seen = new Set([START_NODE]);
  const queue = [START_NODE];

  // ═══════════════════════════════════════════════════════════
  // ☾ A PAID MOON IS AN EDGE FROM NOWHERE — fix, 2026-08-20
  // ═══════════════════════════════════════════════════════════
  // The Crimson Dais has no inbound road ON PURPOSE (caP'n: *"onto Dais
  // has no way in"*) — the only way there is THE MOON, a button in the
  // chrome. Which means this BFS, walking roads out of START_NODE,
  // could never reach dais_jewel or its four gems: 50 of 55 nodes,
  // before AND after the fare was paid.
  //
  // ⚠ AND THAT IS NOT COSMETIC, IT IS A TRAP. THE UN-STRAND in
  // WorldMap yanks any player whose saved position is neither reachable
  // nor cleared back to START_NODE and SAVES it. So: pay 15 Sol Shards,
  // land on the Jewel, lose the final — and the remount that follows
  // moves you off the Dais you just bought and writes it to the save.
  // Reload while standing there and the same thing happens. Every
  // sentence written about "a Dais to grind before you knock again"
  // was false, and the already-paid Moon re-lifting you was the only
  // thing hiding it.
  //
  // The honest fix is to state the edge the Moon actually is. A door
  // that has been PAID FOR is a door, so its avatar node is a second
  // root of the walk — and the four gems come with it down their own
  // `via: "open"` rows. Nothing is reachable before the fare, because
  // `ctx.emperiums` is empty until `openEmperium` commits.
  for (const eid of (ctx.emperiums || [])) {
    const e = EMPERIUM_BY_ID[eid];
    // ⚠ `arrival` FIRST. The Moon lands the player on the WEST corner
    // of the dais, not on the Jewel — see the EMPERIUMS row. Seeding
    // only `avatar` would have left the four wardens outside the walk
    // and the un-strand would move a paying player off the square they
    // just bought, which is the 2026-08-20 bug wearing a new hat.
    for (const id of [e?.arrival, e?.avatar]) {
      if (id && NODE_BY_ID[id] && !seen.has(id)) { seen.add(id); queue.push(id); }
    }
  }

  // ◉ AND AN OPEN EYE IS AN EDGE TOO. The Eye is the second sky door
  // and the ONLY way onto the Eyeland for a player who opened the
  // pupil from the Crimson Dais — they have no beacon and there is no
  // road. Without this seed the un-strand would yank them out of the
  // room the moment the screen remounted.
  if (NODE_BY_ID[EYE_NODE] && eyeOpen(ctx) && !seen.has(EYE_NODE)) {
    seen.add(EYE_NODE); queue.push(EYE_NODE);
  }

  while (queue.length) {
    const cur = queue.shift();
    for (const nxt of adj.get(cur) || []) {
      if (!seen.has(nxt)) { seen.add(nxt); queue.push(nxt); }
    }
  }
  for (const id of ctx.cleared) if (NODE_BY_ID[id]) seen.add(id);
  return seen;
}

// The nodes you can step to RIGHT NOW from where you stand.
// One step at a time — the tactility is the point.
export function neighborsOf(nodeId, ctx) {
  const out = new Map();
  for (const p of PATHS) {
    const s = pathState(p, ctx);
    if (!s.open) continue;
    if (p.from === nodeId) out.set(p.to, { path: p, state: s });
    else if (p.to === nodeId && !p.oneWay) out.set(p.from, { path: p, state: s });
  }
  return out;
}

export function nodeState(nodeId, ctx, reachable) {
  if (ctx.cleared.has(nodeId)) return "cleared";
  // ⚠ THE SURVEYOR. "cleared" still means genuinely beaten — the
  // green tick has to keep telling the truth or the mode is useless
  // for checking progress art. Only "locked" is suppressed.
  if (SURVEYOR)                return "open";
  if (reachable.has(nodeId))   return "open";
  return "locked";
}

// Regions you have laid eyes on. Derived, never stored — a region
// exists the moment one of its nodes is reachable or cleared.
export function discoveredRegions(ctx, reachable) {
  const ids = new Set();
  for (const n of NODES) {
    if (reachable.has(n.id) || ctx.cleared.has(n.id)) ids.add(n.region);
  }
  return REGIONS.filter(r => ids.has(r.id));
}

// Does this node currently offer its secret exit? Answered from
// what you are CARRYING, before a card is played — which is what
// makes a secret findable by experimenting with decks.
export function secretOffered(node, ctx) {
  if (!node?.secretReq) return false;
  if (ctx.secrets.has(node.id)) return false;
  return meetsReq(node.secretReq, ctx).ok;
}

// ═══════════════════════════════════════════════════════════════
// ⭐ THE EGG IS A DOOR — the pupil, without the lap
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-20:
//
//   *"if the easter egg is performed correctly, each node has not
//    only cool easter egg rewards but the ability to teleport to the
//    middle of the eye itself without doing the lap around the eye
//    and getting there the standard way."*
//
// EACH node. Not the ten beacon sources — every node that has an egg
// and had it answered. The ten BEACONS keep their own paired doors
// (a chamber and its private crack) because those are a switchboard
// you earn one plug at a time and they land on the RING, not the
// pupil. This is the other half of the same promise and it lands in
// the middle.
//
// Derived, never authored: a node qualifies because its secret is
// stamped, so writing a new `secretReq` anywhere in the table hands
// that node this door for free and no list can go stale. The pupil
// is `rent_lash` and it is named once, here.
export const EYE_NODE = "ASS-50";

// The six keepers on the ring, and the four wardens on the Dais. Both
// derived from the tables rather than typed, so adding a seventh
// keeper or a fifth warden cannot leave a hand-kept list behind.
export const EYE_RING  = NODES.filter(n => n.region === "eyeland" && n.id !== EYE_NODE).map(n => n.id);
export const DAIS_RING = NODES.filter(n => n.region === "dais" && n.kind !== "throne").map(n => n.id);

// ═══════════════════════════════════════════════════════════════
// ⭐ THE EYE OPENS TWO WAYS — caP'n, 2026-08-21
// ═══════════════════════════════════════════════════════════════
//   *"You can access the middle of the eye by completing the 6 S nodes
//    surrounding it — not just the regular way, the Easter Egg way
//    (keep in mind completing it the easter egg way counts as doing it
//    the standard way, so the player doesn't have to do it twice for
//    no reason; they solved the impossible, they should be rewarded
//    not punished). The other way is by completing the 4 easter eggs
//    on the Crimson Dais."*
//
// So the ring is read off `cleared` and the Dais off `secrets`, and
// the asymmetry is the ruling: beating a keeper counts however you
// beat it, while a Dais warden has to be ANSWERED.
//
// ⚠ AND OPENING IT ONE WAY DOES NOT PAY THE OTHER WAY'S REWARDS.
//   *"This doesn't unlock it for both — for instance, unlocking the
//    middle of the eye through the Crimson Dais doesn't mean you can
//    go to the Eyeland and have the easter eggs unlocked. You can
//    still travel to the middle of eye cause it's unlocked, but you
//    still have to complete the easter eggs if you want the rewards."*
// Which is free, because this function reads the stamps and writes
// nothing. The six ring eggs stay exactly where they were.
export function eyeOpen(ctx) {
  if (!ctx) return false;
  if (EYE_RING.length  && EYE_RING.every(id => ctx.cleared.has(id)))  return true;
  if (DAIS_RING.length && DAIS_RING.every(id => ctx.secrets.has(id))) return true;
  return false;
}

export function eyeStepFrom(node, ctx) {
  if (!node || !ctx) return false;
  if (!node.secretReq) return false;        // no egg, no door
  // ⚠ AND THE ROOM HAS TO BE OPEN. caP'n's 2026-08-20 ruling gave every
  // answered egg a step "to the middle of the eye without doing the lap
  // around the eye" — the LAP, i.e. the ring walk. His 2026-08-21
  // ruling then made that lap (or four Dais eggs) the thing that OPENS
  // the pupil at all. Both are true together: the eye opens by ring or
  // by Dais, and once it has, an answered egg anywhere is a door
  // straight in. Without this line the shortcut walked into a room that
  // had not opened yet.
  if (!eyeOpen(ctx)) return false;
  if (node.id === EYE_NODE) return false;   // already standing in it
  if (!NODE_BY_ID[EYE_NODE]) return false;  // a Minerium with no Eye
  // ⚠ Minerium-local. The door goes to THIS painting's pupil or it
  // does not exist; a shortcut across worlds is WORLD_LINKS' job.
  if ((node.world || FIRST_WORLD) !== (NODE_BY_ID[EYE_NODE].world || FIRST_WORLD)) return false;
  return ctx.secrets.has(node.id);
}

export function childrenFound(ctx) {
  return CHILDREN.filter(c => ctx.found.has(c.id));
}

// ═══════════════════════════════════════════════════════════════
// THE FOUR MARKS — what a pad on the painting MEANS
// ═══════════════════════════════════════════════════════════════
// caP'n's Story Mode ruling, 2026-08-17, written against a map that
// already had the axis it needed and did not know it. Every node
// has carried a `mark` since the pins were placed — chamber, shard,
// cursed, pupil — and until today that word only chose a pin
// glyph. It is the node's ROLE, and these are the four:
//
//   chamber → A LORD'S CHAMBER. "there is a hint when you click on
//             this node, if you can solve the riddle/puzzle and
//             fulfill the specific requirements and then annihilate
//             the opponent of this node correctly based on the
//             puzzle, you unlock the easter egg prize and also it
//             functions as a teleporter."
//             ALL THREE HALVES ALREADY EXIST. The riddle is
//             `secretReq` (checked off what you BRING, before a
//             card is played). The teleporter is the node's BEACON.
//             The prize is `prize` below. Nothing new was built
//             here — a name was put on three things that were
//             already wired to each other.
//
//   shard   → A SHARD KING. "nodes with very hard bosses that carry
//             1 or sometimes even more shards, designated keepers
//             by their Lord they were entrusted to protect it at
//             all costs." Clearing one yields its Sol Shards.
//
//   cursed  → CURSED LAND. "honestly just like a typical battle for
//             now." Deliberately plain, and deliberately named so
//             nobody later mistakes plainness for an oversight.
//
//   pupil   → A SECRET LOCATION. Reached only through a Chamber's
//             beacon. "these areas contain lots of rewards simply
//             annihilating the boss yields fantastic rewards,
//             however, doing so correctly via the puzzle yields
//             even more."
//
// A node with no mark is SPINE — the road itself. Gates, forts and
// the Throne. It is a role, not a gap.
export const MARK_ROLES = {
  chamber: { role: "chamber", label: "Lord's Chamber",  glyph: "◈" },
  // ⭐ RENAMED BY caP'n 2026-08-21: "S = Sol King (previously Shard
  // King)". The `mark` string and the `role` stay `shard` — they are
  // internal keys and `shardsFor`/`SHARD_KEEP` and every stamp already
  // written are keyed off them. What the player READS is the label.
  shard:   { role: "shard",   label: "Sol King",        glyph: "◆" },
  cursed:  { role: "cursed",  label: "Cursed Land",     glyph: "†" },
  pupil:   { role: "secret",  label: "Secret Location", glyph: "◉" },
};

export const nodeRole = (node) => MARK_ROLES[node?.mark]?.role || "spine";

// ── THE VANGUARD TIER ────────────────────────────────────────
// How far in a node is, as an index into REGIONS — which is also
// how much board the opponent is already holding when the Solrend
// opens (NPC.buildVanguard). It reads off the REGIONS table rather
// than off a field on the node, so authoring a new node in an
// existing region inherits its region's pressure for free and can
// never be forgotten. A node that wants to break rank carries
// `tier: N` and overrides it, which is the seam for a brutal early
// Sol King or a merciful late one.
export const regionTier = (regionId) =>
  Math.max(0, REGIONS.findIndex(r => r.id === regionId));
export const vanguardTierFor = (node) =>
  (node?.tier != null ? Number(node.tier) : regionTier(node?.region));
export const roleLabel = (node) => MARK_ROLES[node?.mark]?.label || null;

// A Chamber's hint. Authored per node as `hint`; absent means the
// Chamber has a door but no riddle written for it yet, which is a
// content gap and not a bug — THE FLOOR LAW says the node is
// playable either way, you simply beat it the ordinary way.
export const chamberHint = (node) =>
  (nodeRole(node) === "chamber" ? (node.hint || null) : null);

// ═══════════════════════════════════════════════════════════════
// SOL SHARDS — a key, deliberately NOT a currency
// ═══════════════════════════════════════════════════════════════
// caP'n: "Lord's Arena is never accessible at all unless you
// collect the proper amount of Sol Shards (its ok to share currency
// here don't worry about it ;3) ... you consume 15 Sol Shards."
//
// ⚠ V'AEL'S RULING, FLAGGED FOR VETO. Terror already mints exactly
// two earnable numbers — Chocolate Coins and Divine Crests — and
// IE's two-currency law says a third is a bug. caP'n waived the
// law here; this takes the waiver and then does not need it, which
// is strictly better than spending it.
//
// A Sol Shard is NOT a balance. It is a KEY, and it is stored the
// way keys are stored: an append-only stamp per SHARD KING, at
// `worldMap/shards/{nodeId}`. Your shard count is DERIVED by
// counting stamps minus what a Minerium has consumed. Three things
// follow, all of them free:
//
//   1. It cannot be forged. There is no delta write to intercept —
//      a client that wants a shard has to name a Sol King it has
//      not beaten, and that stamp aborts if it already exists.
//   2. It cannot double-pay. Same transaction guard as every other
//      stamp on this page.
//   3. It never touches the currency lane, so no rate row, no Crest
//      Well entry, no exchange, nothing to register, and the
//      two-currency law is not bent at all.
//
// The cost of that choice, stated honestly: shards are per-world
// and per-profile and can never be spent on anything but a door.
// That is exactly what caP'n described them doing.
export const SHARD_KEEP = "shards";

// How many a given Sol King carries. Default 1; `shards: N` on
// the node overrides it, because caP'n said "1 or sometimes even
// more" and a number that can only ever be 1 is a number waiting
// to become an engine change.
// ⚠ THE FIELD WINS OVER THE MARK, 2026-08-21. This read `nodeRole()`
// FIRST and returned 0 for anything that was not marked `shard` — so
// The Pupil, which is `ASS-50` (an S in caP'n's own code, a Sol King)
// but carries `mark: "pupil"` so its PIN draws as a Secret Location,
// paid nothing. Two Sol Shards vanished out of the world and the HUD
// read 28 of a total that should have been 30, which is the kind of
// number nobody checks because it looks like a number.
//
// The two axes are separate and always were: `mark` is what the pin
// LOOKS like and what the panel CALLS it; `shards` is what the node
// PAYS. A node with the field pays the field. A node with no field
// falls back to its mark, so every existing Sol King still pays one
// without needing the row edited.
export const shardsFor = (node) => {
  if (!node) return 0;
  const n = Number(node.shards);
  if (Number.isFinite(n)) return Math.max(0, n);
  return nodeRole(node) === "shard" ? 1 : 0;
};

// Every shard that exists in this world. Derived, so authoring a
// new Sol King re-prices every Minerium automatically.
export const SHARD_TOTAL = NODES.reduce((n, node) => n + shardsFor(node), 0);

// ═══════════════════════════════════════════════════════════════
// ⭐ THE REACHABILITY AUDIT — the assertion that should have existed
// ═══════════════════════════════════════════════════════════════
// A story mode is a graph, and a graph authored by tracing lines off
// a painting can be disconnected without a single line looking wrong.
// That is exactly what happened: on 2026-08-20 a walk of the real
// tables reached 21 of 55 nodes, the throne among the missing, and
// nothing in the game said a word — because THE SURVEYOR was on and
// every gate was lifted.
//
// So the graph now audits itself. This is deliberately a pure
// function over NODES and PATHS with NO Firebase, no profile and no
// Surveyor: it answers "could a perfect player, starting cold and
// winning everything, ever stand here?" — which is a property of the
// authoring and nothing else.
//
// It is optimistic on purpose. Non-shard requirements (an Ilk, a
// School, a natural Lord, a card, an ELO) are assumed satisfiable,
// because a node this reports as UNREACHABLE is unreachable even in
// the best case. False negatives are impossible; false positives are
// not, which is the correct direction for an alarm.
//
// Wire it anywhere you like — the Debugger renders it today. It costs
// one pass over 101 rows and it is the difference between finding
// this class of bug in a minute and finding it in a shipped build.
export function auditWorldReachability() {
  const byId = NODE_BY_ID;
  const shardsOfId = (id) => shardsFor(byId[id]);

  let cleared = new Set();
  // ⚠ THE FARE COMES OFF THE HELD COUNT, AND IT COMES OFF ONCE. A
  // running `spent` incremented inside the round loop would subtract
  // fifteen again on every pass and report the world unpayable by the
  // third round. A door is paid, and then it is paid.
  const paidDoors = new Set();
  for (let round = 0; round < NODES.length + 2; round++) {
    const earned = [...cleared].reduce((n, id) => n + shardsOfId(id), 0);
    const spent = [...paidDoors].reduce((n, id) => n + (EMPERIUM_BY_ID[id]?.shardCost || 0), 0);
    const held = Math.max(0, earned - spent);
    const adj = new Map();
    const push = (a, b) => { if (!adj.has(a)) adj.set(a, new Set()); adj.get(a).add(b); };
    for (const p of PATHS) {
      const via = p.via || "clear";
      let eventOk = true;
      if (via === "warp" || via === "secret") eventOk = cleared.has(p.from);
      else if (via === "clear") eventOk = cleared.has(p.from) || (!p.oneWay && cleared.has(p.to));
      // ⭐ THE AUDIT MODELS THE SKY DOORS NOW (2026-08-21). It used to
      // model `req.shards` and assume every other requirement was
      // satisfied — which meant it reported the Crimson Dais and the
      // Eyeland as five and seven permanently orphaned nodes, forever,
      // and cleared the one bug it exists to catch by declaring it
      // expected. A tool that reports a known-good state as a failure
      // is a tool nobody reads.
      //
      // What is modelled: the Moon's fare (`req.emperium`, payable
      // once `held` reaches its cost), the Eye's two keys (`req.eye`),
      // and a plain list of prerequisite clears (`req.cleared`).
      // Everything else is still assumed satisfiable, which is honest
      // — an Ilk or a Natural Lord is a DECK question and this
      // function knows nothing about decks.
      let reqOk = true;
      if (p.req) {
        if (typeof p.req.shards === "number") reqOk = reqOk && held >= p.req.shards;
        if (typeof p.req.shardsEarned === "number") reqOk = reqOk && held >= p.req.shardsEarned;
        if (p.req.emperium) {
          const e = EMPERIUM_BY_ID[Array.isArray(p.req.emperium) ? p.req.emperium[0] : p.req.emperium];
          reqOk = reqOk && !!e && held >= e.shardCost;
        }
        if (p.req.eye) {
          // ⚠ `cleared` for the ring, `cleared` for the Dais — and the
          // real eyeOpen() reads SECRETS for the Dais, per caP'n's
          // ruling that a Dais warden must be ANSWERED and not merely
          // beaten. The audit has no secret model, so it assumes the
          // ring route, which is the optimistic half and is the one
          // that keeps this tool free of false alarms. Named so nobody
          // later mistakes it for a faithful copy of the rule.
          reqOk = reqOk && EYE_RING.every(id => cleared.has(id));
        }
        if (p.req.cleared) {
          const need = Array.isArray(p.req.cleared) ? p.req.cleared : [p.req.cleared];
          reqOk = reqOk && need.every(id => cleared.has(id));
        }
      }
      if (!eventOk || !reqOk) continue;
      push(p.from, p.to);
      if (!p.oneWay) push(p.to, p.from);
    }
    const seen = new Set([START_NODE]);
    const q = [START_NODE];
    // ☾ AND THE MOON IS AN EDGE FROM NOWHERE, here as in reachableSet.
    // Once the fare is affordable it seeds the arrival corner of every
    // Minerium door — without this the audit can never enter the Dais
    // and therefore can never see the Jewel, which is the one node it
    // is specifically asked to report on.
    for (const e of EMPERIUMS) {
      if (!paidDoors.has(e.id) && held < e.shardCost) continue;
      paidDoors.add(e.id);
      for (const id of [e.arrival, e.avatar]) {
        if (id && byId[id] && !seen.has(id)) { seen.add(id); q.push(id); }
      }
    }
    while (q.length) {
      const cur = q.shift();
      for (const nxt of adj.get(cur) || []) if (!seen.has(nxt)) { seen.add(nxt); q.push(nxt); }
    }
    for (const id of cleared) seen.add(id);
    const before = cleared.size;
    cleared = seen;
    if (cleared.size === before) break;
  }

  const unreachable = NODES.filter((n) => !cleared.has(n.id));
  const orphanRegions = [...new Set(unreachable.map((n) => n.region))]
    .filter((r) => NODES.filter((n) => n.region === r).every((n) => !cleared.has(n.id)));
  const throne = NODES.find((n) => n.kind === "throne");
  const earnable = [...cleared].reduce((n, id) => n + shardsOfId(id), 0);

  return {
    total: NODES.length,
    reachable: cleared.size,
    unreachable: unreachable.map((n) => ({ id: n.id, name: n.name, region: n.region, kind: n.kind })),
    orphanRegions,
    // The two questions worth asking out loud, because a story that
    // cannot be finished and a door that cannot be paid are the only
    // two failures here that end the game rather than shrink it.
    throneReachable: !!(throne && cleared.has(throne.id)),
    shardsEarnable: earnable,
    shardsTotal: SHARD_TOTAL,
    ok: unreachable.length === 0,
  };
}

// ═══════════════════════════════════════════════════════════════
// THE EMPERIUMS — the Lord's Arena
// ═══════════════════════════════════════════════════════════════
// caP'n: "then wherever you are in the Unstable Overworld, you can
// click the main menu and an option that was usually grayed out is
// highlighted now and reactive that says 'Enter Lord's Minerium'
// where you consume 15 Sol Shards and can now enter the middle area
// of the map, you defeat all 4 according to their puzzles and you
// can now properly Solrend the Lord's Avatar of this Minerium,
// doing so according to it's puzzle yields the greatest rewards."
//
// A TABLE, not a place. The temptation was to hardcode this against
// Overworld_1's geometry, and that is exactly the shape THE
// EXCEPTION LAW warns about: the four wardens are four because this
// painting has four on its Crimson Dais, not because a Minerium
// has four. A second overworld with three wardens and a 20-shard
// door is a row here and nothing else.
//
// Overworld_1's Minerium is the ABANDONED one — the Marsh is
// described in REGIONS as "the outermost ring of the abandoned
// Minerium," so the arena had a Lord before it had a name.
//
// ⚠ THAT LORD IS UNNAMED ON PURPOSE (caP'n, 2026-08-18). `lord: null`
// below is not a missing value — Story Mode names him nowhere, and the
// game's single mention of him lives in the Rulebook. If a name is ever
// wanted here it is ONE field.
//
// ⚠ `id: "xaol"` STAYS. It is an internal key, never rendered, and it
// is persisted at profiles/{uid}/worldMap/emperiums/{id} plus a
// secretReq on the Avatar node — renaming it would orphan every
// player's Minerium progress to rename something nobody can see.
// Its four wardens are the four dais nodes, which is not a
// coincidence: all four already carry violet beacons that land on
// the same pupil, and that pupil is where the Avatar stands.
// ⬡ THEY ARE MINERIUMS NOW (caP'n, 2026-08-18): *"the overworlds are
// now forever referred to as Mineriums — they act as Emperiums but just
// smaller, hence why they're sort of small traversable overworlds."*
//
// The EXPORT keeps its name. `EMPERIUMS` is read by WorldMap and its id
// is persisted at profiles/{uid}/worldMap/emperiums/{id} with a
// secretReq pointing at it — renaming the symbol and the key would
// orphan every player's progress to rename something nobody can see.
// What players READ is the `name` field, and that is a Minerium.
export const EMPERIUMS = [
  {
    id: "xaol",
    name: "The Minerium of Sir H'osh Vrown",
    lord: "Sir H'osh Vrown",
    shardCost: 15,
    // The four. Beaten "according to their puzzles" means each
    // warden's own secretReq — the Minerium does not invent a
    // second puzzle system, it requires the one already on the node.
    wardens: ["AZS-52", "AZS-55", "AZS-54", "AZS-53"],
    avatar: "AZS-56",
    // ⭐ WHERE THE MOON PUTS YOU. caP'n, 2026-08-21: *"When the Player
    // reaches 15 Sol Shards the moon burns fiercely with aura and you
    // can travel to the Crimson Dais where you will start on AZS-52."*
    // NOT the Jewel — the Jewel is the end of the square, not the
    // entrance to it. The first cut of the Moon landed the player on
    // `avatar` and opened the final immediately, which was correct
    // under the old reading and is wrong under this one: there are four
    // wardens to walk now, and the Moon is the door onto the dais, not
    // into the finale.
    arrival: "AZS-52",
    accent: "#ff2266",
    // ☾ THE MOON. What this door LOOKS like in the chrome. caP'n,
    // 2026-08-20: *"the moon in the top left of the Minerium becomes
    // an interactable button that has aura etc so its obvious."*
    // The row carries its own face so a second Minerium's door can be
    // a different object in the sky without a line of renderer moving.
    moon: {
      glyph: "\u263e",
      label: "The Moon",
      shut: "It hangs there. It does not look at you.",
      lit:  "It is looking at you now.",
      open: "Return to the Crimson Jewel",
      done: "Nothing is left on the Dais that wants you.",
    },
  },
];

// ═══════════════════════════════════════════════════════════════
// ☾ ◉ THE SKY DOORS — the two things in the painting you press
// ═══════════════════════════════════════════════════════════════
// Two objects hang in this Minerium's sky and neither of them is a
// node. They are buttons. caP'n, 2026-08-21, on both:
//
//   THE MOON — *"When the Player reaches 15 Sol Shards the moon burns
//   fiercely with aura and you can travel to the Crimson Dais where
//   you will start on AZS-52."* And on its code: *"I also gave the
//   moon a Z because it's used as a teleporter, but don't overthink
//   it — there's no real node there, it only serves as a button to
//   teleport. Just thought it might help giving it a proper spot as
//   well so you know exactly where to place the button, and we can
//   even give it a cool aura colour."*
//
//   THE EYE — *"you'll get a notification as player saying the eye
//   opens to you and you can now click on it proper and travel to the
//   middle of the eyeball."* CLICK ON IT PROPER. The eye in the
//   painting is the second button, and it is what makes the four-Dais-
//   eggs route reachable at all: a player who opened the pupil from
//   the Crimson Dais has no beacon onto the Eyeland and no road, and
//   would otherwise hold a key to a room they cannot walk to.
//
// ⭐ ONE TABLE, BECAUSE THEY ARE ONE MECHANISM. A sky door has a pin
// (so the chrome can sit where the painting keeps it), an aura colour,
// a condition, a landing, and — for the Moon — a fare. Minerium 2's
// sky is a row.
//
// ⚠ AND THEY ARE DOORS, WHICH MEANS BOTH WAYS. Pressing one while
// standing on the far side of it brings you back to where you pressed
// it from. Neither the Dais nor the Eyeland has a road home, so a
// one-way sky door is a trap with a fifteen-shard entry fee — which is
// the exact bug the egg-door shipped with on 2026-08-20 and the exact
// law the beacons were already written under.
export const SKY_DOORS = [
  {
    id: "moon",
    code: "AZT-51",              // caP'n's own code for it. Not a node.
    label: "The Moon",
    glyph: "☾",
    accent: "#ff2266",
    // ⭐ THE MOON BURNS. caP'n, 2026-08-21: *"the moon burns fiercely
    // with aura"*, and 2026-08-22: give it a proper aura colour of its
    // own. So the lit Moon is not the shut Moon with the brightness
    // turned up — it is BONE-WHITE at the core bleeding to BLOOD-RED
    // at the rim, which is the red moon in caP'n's own painting doing
    // what a moon does when it is finally paying attention to you.
    // `accent` stays the chrome colour (borders, text); these two are
    // the burn, and they only ever render while `lit` is true.
    auraCore: "#fff4ef",
    auraRim:  "#c2001f",
    pin: [30, 22],
    emperium: "xaol",            // pressing it pays this door's fare
    target: "AZS-52",
    // ⚠ "INSIDE" IS THE WHOLE REGION FOR THE MOON AND THE ONE NODE FOR
    // THE EYE, and the difference is not fussiness. The Dais has no
    // road off it, so pressing the Moon anywhere on the square has to
    // mean "take me home". The EYELAND does — six ring keepers, walked
    // by beacon — so pressing the Eye while standing on the ring has to
    // mean "take me IN". One shared rule got that second case wrong:
    // the ring is `region: "eyeland"` too, so a player who had just
    // been told the Eye had opened pressed a glowing button and was
    // told they had not come that way.
    homeFrom: "region",
    shut: "It hangs there. It does not look at you.",
    lit:  "It is burning, and it is looking at you now.",
    open: "Return to the Crimson Dais",
    back: "Back the way the Moon brought you",
    done: "Nothing is left on the Dais that wants you.",
  },
  {
    id: "eye",
    code: "ASS-50",
    label: "The Eye",
    glyph: "◉",
    accent: "#ff8fb4",
    pin: [172, 128],
    target: "ASS-50",
    // No fare. Its condition is eyeOpen() and nothing else.
    opensWhen: "eye",
    homeFrom: "node",           // only the pupil itself is "inside"

    shut: "It is looking somewhere over your shoulder.",
    lit:  "It has opened to you.",
    open: "Into the middle of the Eye",
    back: "Back the way the Eye brought you",
    done: "It has seen what it wanted to see.",
  },
];

// The live state of every sky door, in the shape the chrome renders.
// Derived, so a screen never asks two questions to draw one button.
export function skyDoorStates(ctx) {
  return SKY_DOORS.map((d) => {
    const e = d.emperium ? emperiumState(d.emperium, ctx) : null;
    const paid = e ? e.opened : false;
    const canPay = e ? e.canOpen : false;
    const lit = d.emperium ? (canPay && !paid) : (!!ctx && eyeOpen(ctx));
    return {
      ...d,
      cost: e ? e.cost : 0,
      held: e ? e.held : (ctx?.shards || 0),
      paid,
      // `live` is "pressing this does something".
      live: d.emperium ? (paid || canPay) : lit,
      lit,
      // ⚠ `finished`, NOT `done`. This used to be called `done`, which
      // is also the name of the AUTHORED STRING on the row above it —
      // and since the row is spread first, the boolean destroyed the
      // sentence. The Moon's "Nothing is left on the Dais that wants
      // you" and the Eye's "It has seen what it wanted to see" could
      // never be shown by anything, and the renderer's guard against
      // it (`d.done === true ? "" : …`) simply printed an empty line.
      finished: d.emperium ? !!e?.avatarDone : (!!ctx && ctx.cleared.has(d.target)),
    };
  });
}

export const EMPERIUM_BY_ID = Object.fromEntries(EMPERIUMS.map(e => [e.id, e]));

// The whole state of one Minerium, in one object, so a screen never
// has to derive any of this twice.
//
//   held        — shards you are carrying right now
//   cost        — what this door asks
//   opened      — the door has been paid for and stays paid forever
//   wardens     — [{ id, name, cleared, puzzled }] in table order
//   avatarOpen  — every warden beaten by its own puzzle
//   avatarDone  — the Avatar has been Solrended
//   perfect     — the Avatar was taken by ITS puzzle too. caP'n's
//                 "greatest rewards" is this flag and nothing else.
export function emperiumState(id, ctx) {
  const e = EMPERIUM_BY_ID[id];
  if (!e) return null;
  const wardens = e.wardens.map(wid => ({
    id: wid,
    name: NODE_BY_ID[wid]?.name || wid,
    cleared: ctx.cleared.has(wid),
    puzzled: ctx.secrets.has(wid),
  }));
  const opened = ctx.emperiums.has(id);
  return {
    id: e.id, name: e.name, lord: e.lord, accent: e.accent,
    moon: e.moon || null,
    cost: e.shardCost,
    held: ctx.shards,
    canOpen: !opened && ctx.shards >= e.shardCost,
    opened,
    wardens,
    // "defeat all 4 according to their puzzles" — cleared is not
    // enough, the secret stamp is the proof the puzzle was honoured.
    avatarOpen: opened && wardens.every(w => w.puzzled),
    avatarId: e.avatar,
    avatarDone: ctx.cleared.has(e.avatar),
    perfect: ctx.secrets.has(e.avatar),
  };
}

// Every Minerium at once, for the menu row that is "usually grayed
// out" and lights up when the shards are in.
export const emperiumStates = (ctx) => EMPERIUMS.map(e => emperiumState(e.id, ctx));

// ═══════════════════════════════════════════════════════════════
// THE TWO ENDINGS
// ═══════════════════════════════════════════════════════════════
// caP'n: "doing story mode with absolutely no easter eggs will
// yield you one of every card ... then doing every single easter
// egg will yield the super cool version of the sigil."
//
// Both questions are DERIVED from the same stamps the map already
// writes. Nothing new is stored, which means neither ending can
// drift out of step with the map that earned it, and re-walking an
// old save recomputes both for free.
//
// THE EASTER EGGS ARE THE SECRETS. `secretReq` is what a node's
// puzzle already is, and its `secrets/` stamp is already the proof
// the puzzle was answered. Every node that has one is an egg, so
// authoring a new puzzle automatically raises the bar for the
// mythic ending — which is correct, and is the reason this is a
// derived list rather than a hand-kept one that would have gone
// stale the first time a node was added.
export const SECRET_NODES = NODES.filter(n => n.secretReq);

// ── TWO DERIVED VIEWS OF THE MAP ─────────────────────────────────
// Neither is used by the reward pour any more — THE BLUEPRINT LAW
// keyed everything to the node itself, so the pour needs no view of
// the world at all. They stay because other things read them: the
// region index groups the map screen, and the secret list is what
// `allSecretsFound` counts and what the Codex's completion figure is
// built from. Both are derived, never typed, so hanging a `secretReq`
// on a new node teaches them both on the next load.
export const NODES_BY_REGION = NODES.reduce((acc, n) => {
  (acc[n.region] || (acc[n.region] = [])).push(n.id);
  return acc;
}, {});
export const SECRET_NODE_IDS = SECRET_NODES.map(n => n.id);
export const THRONE_NODES = NODES.filter(n => n.kind === "throne");

// ═══════════════════════════════════════════════════════════════
// ◆ WHAT AN EGG IS WORTH, AND WHAT A CLEAR PUTS ON THE SHELF
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-20, on where Divine Crests come from: *"it will be
// from easter eggs, achievements, and challenges."* Easter eggs are
// first on that list and they are THIS map's, so the number lives here
// and not in progression.
//
// TEN, raised from five on 2026-08-21 at cap'n's word: "yes I need a lot
// so I don't have to worry about earning some." THE THREE RANKS still
// price a Lord-rank ACHIEVEMENT at 5 ◆ — that law is untouched; an egg
// is not an achievement, it is the hardest optional thing in Story Mode
// (read the riddle, build a deck around it, AND win), and it is paid
// once, ever, per node. ~28 eggs × 10 = ~280 ◆ across the whole of
// Minerium 1, which is a stocked wallet and not a faucet: the supply is
// FINITE and bounded by the node count. A node may override with
// `prize.divineCrests` the day one deserves more; none does today, and
// nothing is authored to, on purpose — a per-node number is an
// invitation to a stealth economy.
export const EGG_CRESTS = 1;

// ── WHO THE RESERVE PAYS ─────────────────────────────────────────
// ⭐ caP'n, 2026-08-22: *"make it 1 Divine Crest per Easter Egg"* and
// *"maybe the other non-28 nodes don't give you divine crests just
// cards but the 28 you mentioned do, boom."*
//
// Every node now has an egg (see AN EGG ON EVERY NODE), so "the 28"
// needed a rule rather than a list — and when the twenty-eight were
// laid next to the map they turned out to be EXACTLY the twenty-seven
// non-Cursed-Land nodes (every Sol King, every Lord's Chamber, the
// Pupil) plus the Roadhead. That is not a coincidence, it is the
// difference the code letters have always drawn:
//
//   C · Cursed Land    → the egg pays in MYTHIC CARDS alone.
//   S · Sol King       → the egg pays cards AND a Divine Crest.
//   L · Lord's Chamber → the egg pays cards AND a Divine Crest.
//
// So it is a rule, not twenty-seven data rows, and it maintains
// itself: a Sol King added to Minerium Two pays the reserve the day it
// is written, and nobody has to remember to say so.
//
// ⚠ THE ROADHEAD IS THE ONE EXCEPTION AND IT IS DELIBERATE. AAC-01 is
// a Cursed Land, and it is also the first fight in the world and
// therefore the first egg any player will ever solve. Being paid for
// that is how a player learns eggs pay at all. THE EXCEPTION LAW:
// written out, not smoothed away.
export const FIRST_EGG_NODE = "AAC-01";
export const eggCrestsFor = (node) => {
  if (!node) return 0;
  const owed = Number(node.prize?.divineCrests ?? EGG_CRESTS) || 0;
  if (node.id === FIRST_EGG_NODE) return owed;
  return node.mark === "cursed" ? 0 : owed;
};

// The three shelves the Codex and the Grand Archive read. Both screens
// promised the player things that were findable and NOTHING in the
// game wrote a single row to any of them. What fills them now:
//
//   easterEggVariants ← every answered riddle. One per SECRET_NODES.
//   storyVariants     ← every Sol King and every Secret Location
//                       felled. Trophies that remember where they
//                       came from, which is the Codex's own promise.
//   grimoirePages     ← the six Lord's Chambers. A Chamber is where a
//                       Lord kept things; a page out of that Lord's
//                       own Grimoire is what walking out of one is
//                       worth, and it gives the "???" denominator on
//                       the Plaza's tab a real number at last.
//
// All three are DERIVED counts, so authoring a node raises its own
// ceiling and no total can go stale.
export const GRIMOIRE_PAGE_NODES = NODES.filter(n => n.mark === "chamber");
export const GRIMOIRE_PAGE_TOTAL = GRIMOIRE_PAGE_NODES.length;
export const STORY_VARIANT_NODES = NODES.filter(n => n.mark === "shard" || n.mark === "pupil");
export const STORY_VARIANT_TOTAL = STORY_VARIANT_NODES.length;

// Which shelf a node's clear belongs on, or null. One place, so the
// writer below and any screen that wants to preview the shelf can
// never disagree about it.
export const shelfForNode = (node) =>
  node?.mark === "chamber" ? "grimoirePages"
  : (node?.mark === "shard" || node?.mark === "pupil") ? "storyVariants"
  : null;

export const storyComplete   = (ctx) => THRONE_NODES.length > 0 && THRONE_NODES.every(n => ctx.cleared.has(n.id));
export const allSecretsFound = (ctx) => SECRET_NODES.length > 0 && SECRET_NODES.every(n => ctx.secrets.has(n.id));
export const secretsFoundCount = (ctx) => SECRET_NODES.filter(n => ctx.secrets.has(n.id)).length;

// ═══════════════════════════════════════════════════════════════
// THE STAMPS — append-only, transaction-guarded
// ═══════════════════════════════════════════════════════════════
// Every one of these writes an EXACT child path and aborts if the
// stamp already exists, so rewards can never double-pay. No
// full-object set() anywhere in this file. Currency moves only
// through progression.awardCoins (the delta lane).

const wmRef = (uid, sub) => ref(db, `profiles/${uid}/worldMap/${sub}`);

export async function loadWorldProgress(uid) {
  if (!uid) return { cleared: {}, secrets: {}, found: {}, shards: {}, emperiums: {}, gates: {}, ambushes: {}, skyFrom: {}, beats: {}, at: START_NODE };
  try {
    const snap = await get(ref(db, `profiles/${uid}/worldMap`));
    const v = snap.exists() ? snap.val() : {};
    return {
      cleared:   v.cleared   || {},
      secrets:   v.secrets   || {},
      found:     v.found     || {},
      shards:    v.shards    || {},
      emperiums: v.emperiums || {},
      gates:     v.gates     || {},
      ambushes:  v.ambushes  || {},
      // ☾ ◉ Where each sky door picked the player up. See saveSkyFrom.
      skyFrom:   v.skyFrom   || {},
      beats:     v.beats     || {},
      // ⬡ V'AEL'S TOOLS. `tools/{id}` is an append-only stamp — you
      // hold it, once ever. `veil` is the running STEP COUNT of how far
      // they have crept under the cloak, which is a total and not a
      // thing you earned, so it is mutable like `at`. Both are FIELDS
      // under a profile node that already exists: no rules deploy.
      // ⛔ IT USED TO CARRY AN `owed` MAP AND THE VEIL IS FREE — caP'n,
      // 2026-09-15. A save written before that may still have one; it
      // is simply not read. Do not add it back. See vaelsTools.js.
      [TOOL_KEEP]:   v[TOOL_KEEP]   || {},
      [LEDGER_KEEP]: v[LEDGER_KEEP] || { steps: 0 },
      at:        v.at || START_NODE,
    };
  } catch (e) {
    console.warn("[worldMap] load failed:", e);
    return { cleared: {}, secrets: {}, found: {}, shards: {}, emperiums: {}, gates: {}, ambushes: {}, skyFrom: {}, beats: {},
             [TOOL_KEEP]: {}, [LEDGER_KEEP]: { steps: 0 }, at: START_NODE };
  }
}

// ═══════════════════════════════════════════════════════════════
// ⬡ THE DOOR — a fight that is not a node
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-18: *"before you can even enter the Minerium you must
// defeat SHV's Lyt Avatar... yes three separate nodes, well 2. 1 is
// before you even enter so technically no node."*
//
// So it deliberately does NOT go in RAW_NODES. A node has a pin, roads,
// a region and a place in reachability; this has none of those, because
// it happens before the map exists. Putting it in NODES to reuse the
// clear-writing would have given the map a 55th pin nobody can walk to
// and a road graph with an orphan in it.
//
// Instead it is a GATE: same `stamp()` write path (transaction-guarded,
// append-only, idempotent — beat it once, ever), stored beside the
// clears rather than among them.
//
// A gate is a ROW. A second Minerium with a doorman is one more entry
// and no code, which is the whole reason this is a table and not a
// boolean called `hoshDoorCleared`.
export const WORLD_GATES = [
  {
    id: "hosh_door",
    boss: "hosh_lyt",
    minerium: "hosh_vrown",
    // What the player is looking at while they decide to knock.
    title: "The Door",
    line: "Something is standing in the way of the Minerium, and it has been expecting you longer than you have been coming.",
    enter: "Knock",
  },
];

export const GATE_BY_ID = Object.fromEntries(WORLD_GATES.map(g => [g.id, g]));

// The gate barring entry, or null if every one is answered. Today there
// is one; the shape is a list because Minerium 2 will want its own.
export const pendingGate = (progress) =>
  WORLD_GATES.find((g) => !(progress?.gates || {})[g.id]) || null;

// The opponent a gate fields — resolved through the same cast lookup
// every node uses, so the doorman is never re-typed.
export function opponentForGate(gate) {
  const boss = resolveBoss(gate?.boss);
  if (!boss) return null;
  return {
    id: boss.id,
    name: boss.name,
    title: boss.title || null,
    difficulty: boss.difficulty || "Beb",
    starterDeckId: boss.starterDeckId || null,
    npcSkill: boss.npcSkill ?? null,
    temperament: boss.temperament || null,
    accentColor: boss.accentColor || "#c8963c",
    avatarIcon: boss.avatarIcon || "\u25c6",
    dialogue: boss.dialogue || null,
    phases: boss.phases || null,
    isBoss: true,
  };
}

// ═══════════════════════════════════════════════════════════════
// ⬡ THE AMBUSH — a fight that is not a node AND not a door
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-08-20, on how the SECOND Avatar battle actually starts:
//
//   *"once you reach the Sol Shard threshold of 8, V'ael will give
//    you a special treat that restores all your stats, because
//    immediately after SHV's avatar will appear before the player,
//    quick line or two of dialogue and Solrend commences. If you
//    lose, you get sent to plaza and immediately upon re-entry it
//    starts again — you can skip the cutscene as always of course."*
//
// A GATE happens before the map exists. An AMBUSH happens ON the map,
// to a player who was doing something else, and it does not care where
// they are standing. It is the third shape and it needed to be:
//
//   NODE   — a pin. You go to it.
//   GATE   — a doorman. It is in the way.
//   AMBUSH — a threshold. It comes to you.
//
// ⚠ IT IS NOT A ROAD AND IT IS NOT A REQ. The temptation was to hang
// `req: { shards: 8 }` on something — that was the old reading and it
// put the number on two sides of the Crimson Dais ring, where it
// gated a road on the same count that had already sent the fight out
// looking for the player. The count is a TRIGGER. Nothing on the
// painting moves when it trips.
//
// ⚠ AND IT IS NOT LIFTED BY THE SURVEYOR, deliberately. Every other
// gate in this file short-circuits open under the test dial; an
// ambush that fires on a cold save would make the Surveyor unusable
// for walking the map, which is the one job it has. It reads the real
// shard count and nothing else, so testing it means earning eight.
//
// A second Minerium's interception is a row.
export const AMBUSHES = [
  {
    id: "hosh_keep",
    boss: "hosh_lyf",           // The Keep — SHV's second Avatar
    minerium: "hosh_vrown",
    shards: 8,                  // half of what the Moon will ask for
    // ⭐ THE TREAT. It is not a mechanic and it must not become one:
    // a story duel already opens at full Anima and full Khao, so
    // "restores all your stats" is the game telling the truth about
    // what is about to happen to the player rather than a number
    // being written anywhere. If Terror ever grows carry-over between
    // story duels, THIS is the row that has to start meaning it.
    treat: {
      speaker: "V'ael",
      speakerTitle: "The One Who Was Left",
      accent: "#c9a84c",
      lines: [
        "*The world stops being a place for a moment. She is simply there, the way weather is there.*",
        "Eight.",
        "Half of what the Moon is going to ask you for.",
        "Hold still.",
        "*Something warm goes through both of you at once. Every ache you had stopped noticing is gone, and you only learn you were carrying them by the shape of the space they leave.*",
        "[...why now?]",
        "Because he has been watching you count.",
        "And he has decided to stop letting you.",
        "You will not get to sit down first. Consider that my gift and my apology, in that order.",
      ],
    },
  },
];

export const AMBUSH_BY_ID = Object.fromEntries(AMBUSHES.map(a => [a.id, a]));

// The interception owed to this player right now, or null. Takes the
// PROGRESS and the built CTX rather than deriving shards a third time
// — `ctx.shards` is already earned-minus-spent and is the only number
// in the game that answers "how many are you holding."
//
// ⚠ SPENT SHARDS COUNT AGAINST THIS, and that is correct: paying the
// Moon's 15 drops the held count to 0, but by then the ambush has
// long since fired and stamped. A player who somehow reached 15
// without ever passing through 8 does not exist — the Moon costs more
// than the trigger — and if a future Minerium changes that, the stamp
// is what stops the fight arriving after the finale rather than the
// arithmetic.
export function pendingAmbush(progress, ctx) {
  const done = (progress && progress.ambushes) || {};
  const held = (ctx && ctx.shards) || 0;
  return AMBUSHES.find((a) => !done[a.id] && held >= (a.shards || 0)) || null;
}

// Beaten once, ever — same transaction-guarded stamp as gates and
// clears. Returns true only on the first clear.
export async function recordAmbushClear(uid, ambushId) {
  if (!uid || !ambushId) return false;
  return stamp(uid, `ambushes/${ambushId}`);
}

// The opponent an ambush fields. Identical resolution to a gate's, so
// the cast is never re-typed; kept separate because an ambush may one
// day want to override a line the roster carries.
export function opponentForAmbush(ambush) {
  const foe = opponentForGate(ambush);
  if (!foe) return null;
  return { ...foe, isAmbush: true, ambushId: ambush.id };
}

// ── STORY BEATS ─────────────────────────────────────────────────
// A cutscene that is not a duel — a trace of a child, the coin, the
// ending. Same transaction-guarded stamp as everything else here, so a
// beat plays exactly once ever no matter how often its node is
// re-walked. A FIELD under an existing profile node, never a new
// top-level one (IE_FIREBASE_LAW §2), and no `$extra` guard sits at
// the profile level — so this needs no rules deploy.
export async function recordBeat(uid, beatId) {
  if (!uid || !beatId) return false;
  return stamp(uid, `beats/${beatId}`);
}

// Beaten once, ever. Returns true only on the FIRST clear, so a caller
// can tell "you just opened this" from "this was already open."
export async function recordGateClear(uid, gateId) {
  if (!uid || !gateId) return false;
  return stamp(uid, `gates/${gateId}`);
}

// ═══════════════════════════════════════════════════════════════
// ⬡ V'AEL'S TOOLS — handed over once, and how far you crept
// ═══════════════════════════════════════════════════════════════
// caP'n, 2026-09-07: *"after defeating it they are cloaked by V'ael's
// Veil as long as they move at a snails pace they will remain
// undetected... the price for such a powerful tool..s! plural!"*
//
// Two writes, because they are two different KINDS of fact:
//
//   · THE GRANT is a stamp. You were given the Veil, once, ever —
//     same transaction guard as a gate, a clear or a beat, so a
//     double-report cannot hand it over twice.
//   · THE STEP COUNT is an update. It is a running total of how far
//     they have crept, and a total is not something you earned.
//
// ⛔ THERE IS NO DEBT HERE. caP'n, 2026-09-15, asked what the Veil
// costs: *"completely free :3 purely narrative"*. The `owed` map this
// used to write is gone, and the paragraph that used to stand here —
// warning that the day the price bit a duel the ledger would become a
// number worth lying about — is retired with it. THAT DAY IS NOT
// COMING unless caP'n overturns the ruling. See vaelsTools.js.
//
// ⚠ THE STEP COUNT IS CLIENT-WRITTEN AND STAYS FINE, because it buys
// the player nothing at all: it is narrative chrome, not a balance and
// not a debt. Nothing gates on it and nothing spends it.
//
// ⚠ AND `saveVeilLedger` HAS NO CALLERS. It never had any — the step
// count was never actually wired to a walk. It is left here, writing
// only `steps`, as the one obvious place to call from if that chrome
// ever gets built. Do not assume a live writer just because a writer
// exists.
export async function recordToolGrant(uid, toolId) {
  if (!uid || !toolId) return false;
  return stamp(uid, `${TOOL_KEEP}/${toolId}`);
}

export async function saveVeilLedger(uid, ledger) {
  if (!uid || !ledger) return;
  try {
    await update(ref(db, `profiles/${uid}/worldMap/${LEDGER_KEEP}`), {
      steps: Number(ledger.steps) || 0,
    });
  } catch (e) { console.warn("[veil] ledger write failed:", e && e.message); }
}

// Position is the one field here that is genuinely mutable — it is
// where your pawn is standing, not something you earned. Plain
// update on the exact child path.
// ═══════════════════════════════════════════════════════════════
// ☾ ◉ WHERE A SKY DOOR PICKED YOU UP
// ═══════════════════════════════════════════════════════════════
// ⚠ THIS WAS `useState` AND IT STRANDED PEOPLE ON THE CRIMSON DAIS.
//
// A sky door is a door BOTH WAYS, and the way back is "wherever you
// were standing when you pressed it" — which was held in component
// state. WorldMap unmounts for EVERY duel (App swaps to the board), so
// the memory was gone the moment the player fought anything after
// arriving. And the Dais has no road off it: four gems, a centre, and
// nothing else. So the sequence was — pay fifteen Sol Shards, land on
// AZS-52, fight one warden, come back, press the Moon, and be told
// "You did not come this way," with no exit at all.
//
// Worse, the 2026-08-20 fix that seeds a paid Minerium into
// `reachableSet` is what removed the last escape hatch: with the Dais
// now genuinely reachable, THE UN-STRAND no longer fires, so nothing
// rescues the player either.
//
// A field under an existing profile node, keyed BY DOOR — one slot
// shared between two doors meant pressing the Eye from the Dais
// overwrote the Moon's memory of the mainland and left the player
// bouncing between two islands forever.
export async function saveSkyFrom(uid, doorId, nodeId) {
  if (!uid || !doorId) return;
  try {
    await update(ref(db, `profiles/${uid}/worldMap/skyFrom`), { [doorId]: nodeId || null });
  } catch (e) { console.warn("[skyDoor] memory write failed:", e && e.message); }
}

export async function saveWorldPosition(uid, nodeId) {
  if (!uid || !nodeId) return;
  try { await update(ref(db, `profiles/${uid}/worldMap`), { at: nodeId }); }
  catch (e) { console.warn("[worldMap] position write failed:", e); }
}

async function stamp(uid, path) {
  const r = wmRef(uid, path);
  const res = await runTransaction(r, (cur) => (cur ? undefined : Date.now()));
  return res.committed;              // false = already stamped
}

// Clearing a node. `secret` records that this clear was the SECRET
// exit (a node can be cleared normally first and secretly later —
// that is the whole SNES-overworld replay loop, and both stamps
// are independent). Returns what was newly earned.
// One win, counted where every other win is counted. Kept as its own
// function so the read-modify-write is in ONE place and so the
// leaderboard mirror can never be forgotten beside it.
async function bumpStoryWin(uid) {
  try {
    const pRef = ref(db, `profiles/${uid}`);
    const snap = await get(pRef);
    if (!snap.exists()) return;
    const prof = snap.val() || {};
    const next = (prof.totalWins || 0) + 1;
    await update(pRef, { totalWins: next });
    // ⚠ THROUGH progression.mirrorLeaderboard, NOT a hand-rolled write.
    // That function's own banner calls itself "the ONE place where 'a
    // Solrend was just won' is already true" and hangs the H5 FACTION
    // WAR tally off it — so writing the leaderboard node directly here
    // would put the board in step and leave the war tally out of it,
    // which is precisely the fourth-caller-forgets failure that banner
    // predicts.
    await mirrorLeaderboard(uid, prof.name, next);
  } catch (e) {
    // Non-fatal by design: a bookkeeping failure must never cost the
    // player the clear they just earned.
    console.warn("[worldMap] totalWins bump failed:", e && e.message);
  }
}

export async function recordNodeClear(uid, nodeId, { secret = false } = {}) {
  if (!uid || !nodeId) return null;
  const node = NODE_BY_ID[nodeId];
  if (!node) return null;

  const firstClear  = await stamp(uid, `cleared/${nodeId}`);
  const firstSecret = secret ? await stamp(uid, `secrets/${nodeId}`) : false;

  let crests = 0;
  let prize = 0;
  let child = null;
  let shards = 0;

  // ── THE KEEPER'S SHARDS ──────────────────────────────────────
  // Stamped on FIRST CLEAR only, keyed by the Sol King's own node
  // id, which is what makes the whole shard economy idempotent for
  // free: the keeper can only be beaten for the first time once.
  // Note it stamps independently of `firstClear`'s reward branch —
  // a save from before shards existed has cleared Sol Kings and
  // no stamps, and re-walking that world should still arm the door
  // rather than punishing the player for having been early.
  if (shardsFor(node) > 0 && await stamp(uid, `${SHARD_KEEP}/${nodeId}`)) {
    shards = shardsFor(node);
  }

  if (firstClear) {
    // FIRST BLOOD — see progression.grantFirstBlood. A world-map node
    // is a Solrend won, and for a player who starts with Story Mode it
    // is THE first one. This path granted no achievements at all.
    await grantFirstBlood(uid, `worldMap:${nodeId}`);

    // ⭐ A SOLREND IS A SOLREND WHEREVER IT IS FOUGHT.
    // recordNodeClear awarded coins, stamped clears, secrets, shards
    // and children, and poured the Story Set — and never once touched
    // totalWins or the leaderboard. totalWins is written in exactly
    // three places in the game: recordStoryWin (only reachable from
    // Dungeon 1, which is parked), recordPvpResult, and
    // recordFreePlayWin. So the mode a story player spends nearly all
    // of their time in counted for NOTHING: the Codex's TOTAL WINS
    // card undercounted them, the leaderboard never saw them, and the
    // Omnis faction's entire challenge line — "Win 3 Solrends", "Win
    // 10 Solrends", both tracking totalWins — could not be advanced by
    // playing the story at all.
    // First clear only, deliberately: re-walking a cleared node is the
    // map's designed loop and must not farm the counter.
    await bumpStoryWin(uid);

    // ⭐ THE FOUR TROPHY COMPANIONS — INCLUDING THE GAME'S ONLY MYTHIC.
    // progression.unlockCompanion is the only generic companion-unlock
    // write in the codebase and it had ZERO call sites. The only other
    // routes are ensureStarterCompanion (starter deck only) and
    // backfillStoryTrophies, which reads storyProgress.defeatedOpponents
    // — written in exactly one place, inside recordStoryWin, which is
    // only reachable from Dungeon 1, which is parked. So the four story
    // trophies were unobtainable by any route, and the registry
    // deliberately refuses to derive them.
    //
    // The map already knows which fight this was: `node.boss`. The
    // mapping is the same one backfillStoryTrophies declares, kept
    // there as the single source and read here rather than retyped —
    // if a fifth trophy is added, both paths learn it at once.
    // The node's own field first, the boss map second — see the
    // banner on AAS-04 for why the field had to exist.
    const trophy = node.trophy || STORY_TROPHY_BY_BOSS[node.boss];
    if (trophy) {
      try { await unlockCompanion(uid, trophy, `worldMap:${nodeId}`); }
      catch (_) { /* non-fatal — never let a companion cost the clear */ }
    }
    if (node.reward?.crests) {
      crests += node.reward.crests;
      await awardCoins(uid, node.reward.crests, `worldMap:${nodeId}`);
    }
    if (node.reward?.card?.id) {
      await addCardToCollection(
        uid, node.reward.card.id,
        !!node.reward.card.holoTier, node.reward.card.holoTier || null,
        `worldMap:${nodeId}`
      );
    }
    // `node.child` stays supported so a one-off node can still declare
    // one inline; CHILDREN is the table it falls back to. See CHILD_AT.
    const here = node.child || childAtNode(nodeId);
    if (here && await stamp(uid, `found/${here}`)) {
      child = here;
    }
  }

  // ── THE EASTER-EGG PRIZE ─────────────────────────────────────
  // caP'n: "simply annihilating the boss yields fantastic rewards,
  // however, doing so correctly via the puzzle yields even more."
  // `reward` is the fantastic; `prize` is the even more, and it pays
  // ONLY on the first time the puzzle is answered.
  //
  // Gated on `firstSecret`, NOT on `firstClear`, and that is the
  // whole two-tier design in one condition: a node cleared the
  // ordinary way months ago can still be come back to and solved,
  // and the prize is waiting. That is the SNES replay loop the map
  // was built around, and paying only on a first clear would have
  // quietly closed it.
  if (firstSecret && node.prize?.crests) {
    prize = node.prize.crests;
    await awardCoins(uid, prize, `worldMap:prize:${nodeId}`);
  }
  if (firstSecret && node.prize?.card?.id) {
    await addCardToCollection(
      uid, node.prize.card.id,
      !!node.prize.card.holoTier, node.prize.card.holoTier || null,
      `worldMap:prize:${nodeId}`
    );
  }

  // ── ◆ THE EGG PAYS THE RESERVE ───────────────────────────────
  // The first ◆ faucet Terror has ever had. Gated on `firstSecret`
  // exactly like the coin prize above, so the SNES replay loop still
  // works — a node cleared the ordinary way months ago can be come
  // back to and solved, and the Crest is waiting.
  //
  // Non-fatal by design, like every other pour in this function: a
  // currency hiccup must never cost the player the stamp they earned,
  // and the stamp is already down by the time this runs.
  let eggCrests = 0;
  if (firstSecret) {
    const owed = eggCrestsFor(node);   // ⚠ see WHO THE RESERVE PAYS
    if (owed > 0) {
      try {
        await grantCrests(uid, owed, `worldMap:egg:${nodeId}`);
        eggCrests = owed;
      } catch (e) { console.warn("[worldMap] egg Crest pour failed:", e && e.message); }
    }
  }

  // ── ◈ THE SHELVES ────────────────────────────────────────────
  // See WHAT AN EGG IS WORTH above for what goes where. Every one of
  // these is idempotent twice over — `appendShelfItem` aborts on a
  // duplicate id, and it only runs on a first stamp anyway.
  try {
    if (firstSecret) {
      await appendShelfItem(uid, "easterEggVariants", {
        id: `egg:${nodeId}`,
        name: node.name || nodeId,
        dungeon: REGION_BY_ID[node.region]?.name || null,
        isEasterEgg: true,
        at: Date.now(),
      });
    }
    if (firstClear) {
      const shelf = shelfForNode(node);
      if (shelf) {
        await appendShelfItem(uid, shelf, {
          id: `${shelf === "grimoirePages" ? "page" : "trophy"}:${nodeId}`,
          name: node.name || nodeId,
          dungeon: REGION_BY_ID[node.region]?.name || null,
          isEasterEgg: false,
          at: Date.now(),
        });
      }
    }
  } catch (e) {
    // A shelf is a souvenir. It never costs a clear.
    console.warn("[worldMap] shelf write failed:", e && e.message);
  }

  // ── THE MINERIUM PAYS ────────────────────────────────────────
  // ⭐ THE BLUEPRINT LAW (caP'n, 2026-08-22): a node pays the SAME
  // bundle either way — cleared the ordinary way it lands in Fable,
  // cleared the EGG way it lands in Mythic wearing holo_vrown.
  // `minerium1Rewards.NODE_LOOT` is the map; `progression.pourMinerium1`
  // does the writing; this is only the trigger, and it is now two
  // booleans this function already computed at the top.
  //
  // ⭐ AND A NODE EGGED ON THE FIRST ATTEMPT PAYS BOTH, in this one
  // call. caP'n: *"completing it the easter egg way counts as doing it
  // the standard way so the player doesn't have to do it twice for no
  // reason — they solved the impossible, they should be rewarded not
  // punished."* `firstClear` and `firstSecret` are both true on that
  // attempt, and pourMinerium1 reads both.
  //
  // ⚠ NOTE WHAT IS NO LONGER HERE: the fresh loadWorldProgress read.
  // The old wedge system had to re-read the whole world after every
  // clear to find out whether this stamp had completed a REGION or
  // closed the 100% gate. The blueprint has no gates, so a clear costs
  // one node's worth of work and nothing else. That read was the
  // single most expensive thing in this function.
  let storySet = null;
  if (firstClear || firstSecret) {
    try {
      const pour = await pourMinerium1(uid, nodeId, { firstClear, firstSecret });
      if (pour) {
        // The ribbon wants one sentence, so it says the rarer of the
        // two things that just happened — a Mythic pour outranks a
        // Fable one — and names the room, because "where did this come
        // from" is the same question the source sigil exists to answer.
        storySet = {
          tier: pour.mythic ? "mythic" : "fable",
          both: !!(pour.mythic && pour.fable),
          poured: pour.fable + pour.mythic,
          cosmetics: pour.cosmetics,
          label: pour.label || labelForNode(nodeId),
        };
      }
    } catch (e) {
      // Never let a reward pour break the clear that earned it. The
      // stamps are already down; the node is due again next time.
      console.warn("[worldMap] Minerium pour failed:", e);
    }
  }

  await saveWorldPosition(uid, nodeId);
  return { firstClear, firstSecret, crests, prize, eggCrests, child, shards, storySet, role: nodeRole(node) };
}

// ── PAYING A LORD'S DOOR ──────────────────────────────────────
// One transaction on the Minerium's own node, recording WHAT WAS
// PAID rather than decrementing a balance — there is no balance to
// decrement (see SOL SHARDS). Aborts if the door is already open,
// so a double-tap can never charge twice, and the shards a player
// is carrying are recomputed from earned-minus-paid on the next
// ctx build.
//
// It re-derives the held count from the DB rather than trusting the
// caller's ctx: the button that calls this is on a screen that has
// been open for a while, and a stale snapshot is how a door gets
// opened for free.
export async function openEmperium(uid, emperiumId) {
  const e = EMPERIUM_BY_ID[emperiumId];
  if (!uid || !e) return { ok: false, why: "No such Minerium." };

  const snap = await get(ref(db, `profiles/${uid}/worldMap`));
  const v = snap.exists() ? snap.val() : {};
  if (v?.emperiums?.[emperiumId]) return { ok: false, why: "Already open." };

  const earned = Object.keys(v?.shards || {})
    .reduce((n, nodeId) => n + shardsFor(NODE_BY_ID[nodeId]), 0);
  const spent = Object.values(v?.emperiums || {})
    .reduce((n, rec) => n + (Number(rec?.paid) || 0), 0);
  const held = Math.max(0, earned - spent);
  if (held < e.shardCost) {
    return { ok: false, why: `${e.shardCost - held} more Sol Shards.`, held, cost: e.shardCost };
  }

  const r = wmRef(uid, `emperiums/${emperiumId}`);
  const res = await runTransaction(r, (cur) =>
    (cur ? undefined : { paid: e.shardCost, at: Date.now() })
  );
  if (!res.committed) return { ok: false, why: "Already open." };
  return { ok: true, held: held - e.shardCost, cost: e.shardCost };
}

// ═══════════════════════════════════════════════════════════════
// OPEN SEAMS (deliberate, named, so nobody has to rediscover them)
// ═══════════════════════════════════════════════════════════════
// · NODE POSITIONS ARE AUTHORED, NOT MEASURED. Every mx/my below
//   was placed against the painted pads by eye. `world_pinner.html`
//   is the instrument for correcting them — open it, drag, copy the
//   block it prints, paste it over the NODES array. That loop is
//   the intended workflow whenever the art changes.
// · `bend` is wired and used on exactly one path (drowned_arch →
//   vaults_door, the long haul up the map). Every other road is a
//   straight line today. Tracing more of them onto the painted
//   roads is pure content work and needs no code.
// · secretReq.result — a secret earned by HOW you won (flawless,
//   under N turns, never Fractured) rather than what you brought.
//   Needs the match to report a result shape back through meta.
//   The resolver already recurses; only buildCtx and the match
//   hand-off need the extra field.
// · CHILDREN[].name — null until caP'n names them.
// · region.req — regions currently unlock by pure path reachability.
//   The field is free the day a region should need something else.
// · A node's `reward.card` and `prize.card` are both wired and
//   unused; every drop on this map is coins until an actual card
//   table is authored. `prize` itself IS now live (2026-08-17) and
//   pays on first secret, not on first clear.
// · The painted map names the Graveyard TWICE (two islands, one
//   name) and leaves the Eye unnamed. Region IV owns both graveyard
//   islands; the Eye is the Silver Lining. Both are naming calls,
//   reversible in the REGIONS table above with no other edit.
